import React, { useState, useEffect, FormEvent, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Dumbbell, Play, Square, Plus, Clock, ArrowLeft, Search, ChevronDown, Pencil, Trash2, Calendar as CalendarIcon, User, Copy } from 'lucide-react';
import { useWorkoutStore } from '@/store/workoutStore';
import { useUIStore } from '@/store/uiStore';
import { machineApi, bodyweightApi, floorPlanApi, workoutApi, exerciseApi } from '@/services/api';
import { Card, Button, Modal, Calendar } from '@/components/common';
import { MachineCard } from '@/components/workout';
import type { Machine, BodyweightExercise, GymFloorPlan, Workout, ExerciseSet, BodyweightSubcategory } from '@/types';
import { machineMuscleGroupMatches } from '@/lib/muscleGroups';

const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000; // 30 perc tétlenség után figyelmeztetés
const INACTIVITY_AUTO_END_SECONDS = 60;

const WorkoutSession = () => {
  const {
    activeWorkout,
    isLoading,
    loadActiveWorkout,
    startWorkout,
    endWorkout,
    addExercise,
    addSet,
    updateSet,
    deleteSet,
    updateExerciseInWorkout,
    deleteExercise,
  } = useWorkoutStore();
  const { showToast } = useUIStore();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const viewWorkoutId = searchParams.get('view');
  const isBackdateMode = searchParams.get('backdate') === 'true';
  const isAiApplied = searchParams.get('aiApplied') === 'true';

  // View mode state (for viewing past workouts)
  const [viewWorkout, setViewWorkout] = useState<Workout | null>(null);
  const [loadingView, setLoadingView] = useState(false);
  const [activeWorkoutCalendarOpen, setActiveWorkoutCalendarOpen] = useState(false);

  const [showMachineModal, setShowMachineModal] = useState(false);
  const [machines, setMachines] = useState<Machine[]>([]);
  const [bodyweightExercises, setBodyweightExercises] = useState<BodyweightExercise[]>([]);
  const [machineTypes, setMachineTypes] = useState<string[]>([]);
  const [floorPlans, setFloorPlans] = useState<GymFloorPlan[]>([]);
  const [loadingMachines, setLoadingMachines] = useState(false);
  const [machineSearchQuery, setMachineSearchQuery] = useState('');
  const [machineSelectedType, setMachineSelectedType] = useState('');
  const [machineSelectedFloorPlanId, setMachineSelectedFloorPlanId] = useState('');
  
  // Tab state for exercise selection
  const [exerciseTabType, setExerciseTabType] = useState<'equipment' | 'bodyweight'>('equipment');
  const [bodyweightSubcategory, setBodyweightSubcategory] = useState<BodyweightSubcategory | ''>('');

  const [showSetModal, setShowSetModal] = useState(false);
  const [selectedExerciseId, setSelectedExerciseId] = useState<string | null>(null);
  const [setType, setSetType] = useState<'standard' | 'cardio' | 'dualHand'>('standard');
  
  // Standard set fields
  const [reps, setReps] = useState(() => localStorage.getItem('defaultReps') || '12');
  const [weight, setWeight] = useState('');
  
  // Cardio set fields
  const [durationMinutes, setDurationMinutes] = useState('5');
  const [durationSeconds, setDurationSeconds] = useState('0');
  const [resistanceLevel, setResistanceLevel] = useState('');
  
  // Dual hand set fields
  const [leftReps, setLeftReps] = useState(() => localStorage.getItem('defaultReps') || '12');
  const [rightReps, setRightReps] = useState(() => localStorage.getItem('defaultReps') || '12');
  const [leftWeight, setLeftWeight] = useState('');
  const [rightWeight, setRightWeight] = useState('');
  
  const [difficulty, setDifficulty] = useState(3);
  const [savingSet, setSavingSet] = useState(false);

  // Edit set state
  const [editingSetId, setEditingSetId] = useState<string | null>(null);
  const [, setEditingExerciseId] = useState<string | null>(null);
  const [showEditSetModal, setShowEditSetModal] = useState(false);

  // Delete set state
  const [deletingSetId, setDeletingSetId] = useState<string | null>(null);
  const [deletingExerciseId, setDeletingExerciseId] = useState<string | null>(null);
  const [showDeleteSetModal, setShowDeleteSetModal] = useState(false);
  const [deletingSet, setDeletingSet] = useState(false);

  // View mode editing states
  const [showViewExerciseSwapModal, setShowViewExerciseSwapModal] = useState(false);
  const [viewSwapExerciseId, setViewSwapExerciseId] = useState<string | null>(null);
  const [exerciseSwapContext, setExerciseSwapContext] = useState<'view' | 'active' | null>(null);
  const [showDeleteExerciseModal, setShowDeleteExerciseModal] = useState(false);
  const [deleteExerciseId, setDeleteExerciseId] = useState<string | null>(null);
  const [deleteExerciseName, setDeleteExerciseName] = useState('');
  const [deletingExercise, setDeletingExercise] = useState(false);
  const [showInactivityModal, setShowInactivityModal] = useState(false);
  const [inactivityCountdown, setInactivityCountdown] = useState(INACTIVITY_AUTO_END_SECONDS);
  const lastActivityRef = useRef(Date.now());
  const inactivityTriggeredRef = useRef(false);
  const [showViewSetFormModal, setShowViewSetFormModal] = useState(false);
  const [viewSetFormMode, setViewSetFormMode] = useState<'add' | 'edit'>('add');
  const [viewSavingSet, setViewSavingSet] = useState(false);
  const [showViewDeleteConfirmModal, setShowViewDeleteConfirmModal] = useState(false);
  const [viewDeletingSet, setViewDeletingSet] = useState(false);
  // View mode exercise notes state
  const [viewExerciseNotesMap, setViewExerciseNotesMap] = useState<Record<string, string | null>>({});


  const [bodyWeight, setBodyWeight] = useState('');
  const [startingWorkout, setStartingWorkout] = useState(false);
  const [elapsedTime, setElapsedTime] = useState('00:00:00');
  const [activeExerciseId, setActiveExerciseId] = useState<string | null>(null);
  const [activeExerciseStartTime, setActiveExerciseStartTime] = useState<number | null>(null);
  // Accumulated seconds per exercise (from DB or previous tracking before switching)
  const [exerciseAccumulatedSeconds, setExerciseAccumulatedSeconds] = useState<Record<string, number>>({});
  const [, setTick] = useState(0); // Force re-render for timers
  // Exercise notes state (local cache to avoid refetching)
  const [exerciseNotesMap, setExerciseNotesMap] = useState<Record<string, string | null>>({});

  // AI suggestions from sessionStorage (set by WorkoutBuilder when applying a plan)
  const [aiSuggestions, setAiSuggestions] = useState<Record<string, { name: string; muscleGroup: string; sets: { reps?: number; weight?: number; durationSeconds?: number; restSeconds: number }[]; reasoning?: string }>>({});
  
  // Calculate current elapsed seconds for an exercise (accumulated + live time if active)
  const getExerciseElapsedSeconds = useCallback((exerciseId: string): number => {
    const accumulated = exerciseAccumulatedSeconds[exerciseId] || 0;
    if (activeExerciseId === exerciseId && activeExerciseStartTime) {
      const liveSeconds = Math.floor((Date.now() - activeExerciseStartTime) / 1000);
      return accumulated + liveSeconds;
    }
    return accumulated;
  }, [exerciseAccumulatedSeconds, activeExerciseId, activeExerciseStartTime]);
  
  // Mood states
  const [startMood, setStartMood] = useState(4); // Default: "Jó"
  const [endMood, setEndMood] = useState(4); // Default: "Jó"
  const [workoutNotes, setWorkoutNotes] = useState('');
  const [showConfirmEndModal, setShowConfirmEndModal] = useState(false);
  const [showEndModal, setShowEndModal] = useState(false);
  const [endingWorkout, setEndingWorkout] = useState(false);
  const [abandoningWorkout, setAbandoningWorkout] = useState(false);
  const [showAbandonConfirmModal, setShowAbandonConfirmModal] = useState(false);

  // Backdate states (only used in backdate mode from history page)
  const [backdateDate, setBackdateDate] = useState(() => {
    const now = new Date();
    return now.toISOString().split('T')[0]; // YYYY-MM-DD
  });
  const [backdateTime, setBackdateTime] = useState(() => {
    const now = new Date();
    return `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
  });
  
  // Backdate end modal states
  const [backdateDurationHours, setBackdateDurationHours] = useState('1');
  const [backdateDurationMinutes, setBackdateDurationMinutes] = useState('0');
  const [backdateTimeDistribution, setBackdateTimeDistribution] = useState<'proportional' | 'manual'>('proportional');
  const [backdateExerciseTimes, setBackdateExerciseTimes] = useState<Record<string, number>>({});

  // Mood options with emojis
  const moodOptions = [
    { level: 1, emoji: '😫', label: 'Fáradt' },
    { level: 2, emoji: '😕', label: 'Gyenge' },
    { level: 3, emoji: '😐', label: 'Átlagos' },
    { level: 4, emoji: '😊', label: 'Jó' },
    { level: 5, emoji: '💪', label: 'Szuper' },
  ];

  // Format elapsed time as HH:MM:SS
  const formatElapsedTime = useCallback((startTime: string) => {
    const start = new Date(startTime).getTime();
    const now = Date.now();
    const diff = Math.floor((now - start) / 1000);
    
    const hours = Math.floor(diff / 3600);
    const minutes = Math.floor((diff % 3600) / 60);
    const seconds = diff % 60;
    
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }, []);

  // Load AI suggestions from sessionStorage when coming from workout builder
  useEffect(() => {
    if (isAiApplied) {
      try {
        const stored = sessionStorage.getItem('aiWorkoutSuggestions');
        if (stored) {
          setAiSuggestions(JSON.parse(stored));
        }
      } catch { /* ignore */ }
    }
  }, [isAiApplied]);

  useEffect(() => {
    if (viewWorkoutId) {
      // Load specific workout for viewing
      const loadViewWorkout = async () => {
        setLoadingView(true);
        try {
          const response = await workoutApi.getById(viewWorkoutId);
          if (response.success && response.data) {
            setViewWorkout(response.data.workout);
            // Initialize view exercise notes from loaded workout
            const notesMap: Record<string, string | null> = {};
            response.data.workout.exercises.forEach(ex => {
              notesMap[ex.id] = ex.notes ?? null;
            });
            setViewExerciseNotesMap(notesMap);
          }
        } catch (error) {
          console.error('Failed to load workout:', error);
          showToast('Hiba az edzés betöltésekor.', 'error');
          navigate('/history');
        } finally {
          setLoadingView(false);
        }
      };
      loadViewWorkout();
      loadFloorPlans();
    } else {
      // Normal mode - load active workout
      loadActiveWorkout();
      loadFloorPlans();
    }
  }, [loadActiveWorkout, viewWorkoutId, showToast, navigate]);

  useEffect(() => {
    if (activeWorkout && !viewWorkoutId) {
      setActiveWorkoutCalendarOpen(false);
    }
  }, [activeWorkout?.id, viewWorkoutId]);

  // Track previous exercise count to detect new exercises
  const [prevExerciseCount, setPrevExerciseCount] = useState(0);

  // Initialize accumulated times and notes from database when workout loads
  useEffect(() => {
    if (activeWorkout && activeWorkout.exercises.length > 0) {
      // Load accumulated times from database
      const times: Record<string, number> = {};
      const notes: Record<string, string | null> = {};
      activeWorkout.exercises.forEach(exercise => {
        // Use elapsedSeconds from database if available, keep existing if already tracked
        if (exerciseAccumulatedSeconds[exercise.id] === undefined) {
          times[exercise.id] = (exercise as { elapsedSeconds?: number }).elapsedSeconds || 0;
        } else {
          times[exercise.id] = exerciseAccumulatedSeconds[exercise.id];
        }
        // Initialize notes from database if not already cached locally
        if (exerciseNotesMap[exercise.id] === undefined) {
          notes[exercise.id] = exercise.notes ?? null;
        } else {
          notes[exercise.id] = exerciseNotesMap[exercise.id];
        }
      });
      setExerciseAccumulatedSeconds(times);
      setExerciseNotesMap(prev => ({ ...prev, ...notes }));
      
      const currentCount = activeWorkout.exercises.length;
      const newestExercise = activeWorkout.exercises[activeWorkout.exercises.length - 1];
      
      // Auto-switch to newest exercise when a new one is added
      if (currentCount > prevExerciseCount && prevExerciseCount > 0) {
        // Save current exercise time before switching (accumulate live time)
        if (activeExerciseId && activeExerciseStartTime) {
          const liveSeconds = Math.floor((Date.now() - activeExerciseStartTime) / 1000);
          const totalElapsed = (exerciseAccumulatedSeconds[activeExerciseId] || 0) + liveSeconds;
          if (totalElapsed > 0) {
            exerciseApi.updateTime(activeExerciseId, totalElapsed).catch(() => {});
            setExerciseAccumulatedSeconds(prev => ({
              ...prev,
              [activeExerciseId]: totalElapsed
            }));
          }
        }
        // Switch to new exercise
        setActiveExerciseId(newestExercise.id);
        setActiveExerciseStartTime(Date.now());
      } else if (!activeExerciseId) {
        const targetExercise = isAiApplied
          ? activeWorkout.exercises[0]
          : newestExercise;
        setActiveExerciseId(targetExercise.id);
        setActiveExerciseStartTime(Date.now());
      }
      
      setPrevExerciseCount(currentCount);
    } else {
      setActiveExerciseId(null);
      setActiveExerciseStartTime(null);
      setPrevExerciseCount(0);
    }
  }, [activeWorkout?.id, activeWorkout?.exercises.length]);

  // Live timer for active workout (disabled in backdate mode)
  useEffect(() => {
    if (!activeWorkout || isBackdateMode) return;
    
    const updateTimers = () => {
      // Update workout elapsed time
      setElapsedTime(formatElapsedTime(activeWorkout.startTime));
      
      // Force re-render
      setTick(t => t + 1);
    };
    
    // Update every second
    const interval = setInterval(updateTimers, 1000);
    
    return () => clearInterval(interval);
  }, [activeWorkout, formatElapsedTime, isBackdateMode]);

  // Timer for active exercise - just triggers re-render every second to update display (disabled in backdate mode)
  // The actual elapsed time is calculated from activeExerciseStartTime, so it works even when app is backgrounded
  useEffect(() => {
    if (!activeExerciseId || !activeExerciseStartTime || isBackdateMode) return;
    
    const interval = setInterval(() => {
      // Force re-render to update the displayed time (calculated from start time)
      setTick(t => t + 1);
    }, 1000);
    
    return () => clearInterval(interval);
  }, [activeExerciseId, activeExerciseStartTime, isBackdateMode]);

  // Save active exercise time to database every 10 seconds (disabled in backdate mode)
  useEffect(() => {
    if (!activeExerciseId || !activeExerciseStartTime || isBackdateMode) return;
    
    const saveTime = async () => {
      const elapsed = getExerciseElapsedSeconds(activeExerciseId);
      if (elapsed > 0) {
        try {
          await exerciseApi.updateTime(activeExerciseId, elapsed);
        } catch {
          // Silent fail - will retry next interval
        }
      }
    };
    
    const interval = setInterval(saveTime, 10000); // Save every 10 seconds
    
    return () => {
      clearInterval(interval);
      // Save on cleanup (when exercise changes or component unmounts)
      saveTime();
    };
  }, [activeExerciseId, activeExerciseStartTime, getExerciseElapsedSeconds, isBackdateMode]);

  // Update exercise notes
  const handleExerciseNotesUpdate = useCallback(async (exerciseId: string, notes: string | null) => {
    try {
      await exerciseApi.updateNotes(exerciseId, notes);
      setExerciseNotesMap(prev => ({ ...prev, [exerciseId]: notes }));
      lastActivityRef.current = Date.now();
    } catch (error) {
      console.error('Failed to save exercise notes:', error);
      showToast('Hiba a megjegyzés mentésekor.', 'error');
      throw error;
    }
  }, [showToast]);

  // Update exercise notes in view mode (past workouts)
  const handleViewExerciseNotesUpdate = useCallback(async (exerciseId: string, notes: string | null) => {
    try {
      await exerciseApi.updateNotes(exerciseId, notes);
      setViewExerciseNotesMap(prev => ({ ...prev, [exerciseId]: notes }));
      // Also update viewWorkout state so the data stays consistent
      setViewWorkout(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          exercises: prev.exercises.map(ex =>
            ex.id === exerciseId ? { ...ex, notes: notes } : ex
          ),
        };
      });
    } catch (error) {
      console.error('Failed to save exercise notes:', error);
      showToast('Hiba a megjegyzés mentésekor.', 'error');
      throw error;
    }
  }, [showToast]);

  // Start exercise timer
  const startExerciseTimer = async (exerciseId: string) => {
    // Save current exercise time before switching and accumulate the live time
    if (activeExerciseId && activeExerciseId !== exerciseId && activeExerciseStartTime) {
      const liveSeconds = Math.floor((Date.now() - activeExerciseStartTime) / 1000);
      const totalElapsed = (exerciseAccumulatedSeconds[activeExerciseId] || 0) + liveSeconds;
      if (totalElapsed > 0) {
        // Update accumulated time in state
        setExerciseAccumulatedSeconds(prev => ({
          ...prev,
          [activeExerciseId]: totalElapsed
        }));
        try {
          await exerciseApi.updateTime(activeExerciseId, totalElapsed);
        } catch {
          // Silent fail
        }
      }
    }
    setActiveExerciseId(exerciseId);
    setActiveExerciseStartTime(Date.now());
  };

  // Stop exercise timer
  const stopExerciseTimer = async () => {
    // Save current exercise time and accumulate the live time
    if (activeExerciseId && activeExerciseStartTime) {
      const liveSeconds = Math.floor((Date.now() - activeExerciseStartTime) / 1000);
      const totalElapsed = (exerciseAccumulatedSeconds[activeExerciseId] || 0) + liveSeconds;
      if (totalElapsed > 0) {
        // Update accumulated time in state
        setExerciseAccumulatedSeconds(prev => ({
          ...prev,
          [activeExerciseId]: totalElapsed
        }));
        try {
          await exerciseApi.updateTime(activeExerciseId, totalElapsed);
        } catch {
          // Silent fail
        }
      }
    }
    setActiveExerciseId(null);
    setActiveExerciseStartTime(null);
  };

  const loadFloorPlans = async () => {
    try {
      const response = await floorPlanApi.getAll();
      if (response.success && response.data) {
        setFloorPlans(response.data.floorPlans);
      }
    } catch (error) {
      console.error('Failed to load floor plans:', error);
    }
  };

  const loadMachines = async () => {
    setLoadingMachines(true);
    try {
      const [machinesResponse, bodyweightResponse, floorPlansResponse, typesResponse] = await Promise.all([
        machineApi.getAll({ sortBy: 'usage' }),
        bodyweightApi.getAll(),
        floorPlanApi.getAll(),
        machineApi.getTypes(),
      ]);

      if (machinesResponse.success && machinesResponse.data) {
        setMachines(machinesResponse.data.machines);
      }

      if (bodyweightResponse.success && bodyweightResponse.data) {
        setBodyweightExercises(bodyweightResponse.data.exercises);
      }

      if (floorPlansResponse.success && floorPlansResponse.data) {
        setFloorPlans(floorPlansResponse.data.floorPlans);
      }

      if (typesResponse.success && typesResponse.data) {
        setMachineTypes(typesResponse.data.types);
      }
    } catch (error) {
      console.error('Failed to load machines:', error);
    } finally {
      setLoadingMachines(false);
    }
  };

  const handleStartWorkout = async () => {
    setStartingWorkout(true);
    try {
      // Build startTime if in backdate mode
      let startTime: string | undefined;
      if (isBackdateMode) {
        const dateTime = new Date(`${backdateDate}T${backdateTime}:00`);
        if (dateTime > new Date()) {
          showToast('A visszadátumozás nem lehet jövőbeli dátum!', 'error');
          setStartingWorkout(false);
          return;
        }
        startTime = dateTime.toISOString();
      }
      
      await startWorkout(startMood, startTime);
      showToast(isBackdateMode ? 'Korábbi edzés rögzítése elindítva!' : 'Edzés elindítva!', 'success');
    } catch (error) {
      console.error('Failed to start workout:', error);
      showToast('Hiba az edzés indításakor.', 'error');
    } finally {
      setStartingWorkout(false);
    }
  };

  const handleOpenConfirmEndModal = () => {
    setShowConfirmEndModal(true);
  };

  const handleConfirmEnd = () => {
    setShowConfirmEndModal(false);
    setEndMood(4); // Default: "Jó"
    
    // In backdate mode, initialize exercise times for manual entry
    if (isBackdateMode && activeWorkout) {
      const times: Record<string, number> = {};
      activeWorkout.exercises.forEach(exercise => {
        times[exercise.id] = 5; // Default 5 minutes per exercise
      });
      setBackdateExerciseTimes(times);
    }
    
    setShowEndModal(true);
  };

  const handleAbandonWorkout = async () => {
    if (!activeWorkout) return;
    
    setAbandoningWorkout(true);
    try {
      await workoutApi.delete(activeWorkout.id);
      setShowAbandonConfirmModal(false);
      setShowConfirmEndModal(false);
      navigate('/workout', { replace: true });
      window.location.reload();
    } catch (error) {
      console.error('Failed to abandon workout:', error);
      alert('Hiba történt az edzés megszakításakor.');
    } finally {
      setAbandoningWorkout(false);
    }
  };

  const handleEndWorkout = async () => {
    setEndingWorkout(true);
    try {
      if (isBackdateMode && activeWorkout) {
        // Backdate mode: calculate endTime and exercise times
        const totalDurationSeconds = (parseInt(backdateDurationHours, 10) || 0) * 3600 + 
                                     (parseInt(backdateDurationMinutes, 10) || 0) * 60;
        const startTime = new Date(activeWorkout.startTime);
        const endTime = new Date(startTime.getTime() + totalDurationSeconds * 1000);
        
        // Calculate exercise times
        let exerciseTimes: Record<string, number> = {};
        
        if (backdateTimeDistribution === 'proportional') {
          // Distribute time proportionally (equal for now, could be based on sets)
          const exerciseCount = activeWorkout.exercises.length;
          const timePerExercise = Math.floor(totalDurationSeconds / Math.max(exerciseCount, 1));
          activeWorkout.exercises.forEach(exercise => {
            exerciseTimes[exercise.id] = timePerExercise;
          });
        } else {
          // Manual times (in minutes, convert to seconds)
          activeWorkout.exercises.forEach(exercise => {
            exerciseTimes[exercise.id] = (backdateExerciseTimes[exercise.id] || 0) * 60;
          });
        }
        
        // Save exercise times
        const savePromises = activeWorkout.exercises.map(exercise => {
          const elapsed = exerciseTimes[exercise.id];
          if (elapsed !== undefined && elapsed > 0) {
            return exerciseApi.updateTime(exercise.id, elapsed).catch(() => {});
          }
          return Promise.resolve();
        });
        await Promise.all(savePromises);
        
        // End workout with custom endTime
        await endWorkout({
          bodyWeight: bodyWeight ? parseFloat(bodyWeight) : undefined,
          endMood: endMood,
          notes: workoutNotes.trim() || undefined,
          endTime: endTime.toISOString(),
        });
      } else {
        // Normal mode: save live tracked times (using calculated elapsed time)
        if (activeWorkout) {
          const savePromises = activeWorkout.exercises.map(exercise => {
            const elapsed = getExerciseElapsedSeconds(exercise.id);
            if (elapsed > 0) {
              return exerciseApi.updateTime(exercise.id, elapsed).catch(() => {});
            }
            return Promise.resolve();
          });
          await Promise.all(savePromises);
        }
        
        await endWorkout({
          bodyWeight: bodyWeight ? parseFloat(bodyWeight) : undefined,
          endMood: endMood,
          notes: workoutNotes.trim() || undefined,
        });
      }
      
      showToast('Edzés befejezve!', 'success');
      setShowEndModal(false);
      setBodyWeight('');
      setWorkoutNotes('');
      navigate('/history');
    } catch (error) {
      console.error('Failed to end workout:', error);
      showToast('Hiba az edzés befejezésekor.', 'error');
    } finally {
      setEndingWorkout(false);
    }
  };

  const handleOpenMachineModal = () => {
    setMachineSearchQuery('');
    setMachineSelectedType('');
    setMachineSelectedFloorPlanId('');
    setExerciseTabType('equipment');
    setBodyweightSubcategory('');
    loadMachines();
    setShowMachineModal(true);
    lastActivityRef.current = Date.now();
  };

  const recordActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    if (showInactivityModal) {
      setShowInactivityModal(false);
      inactivityTriggeredRef.current = false;
      setInactivityCountdown(INACTIVITY_AUTO_END_SECONDS);
    }
  }, [showInactivityModal]);

  // Inaktivitás figyelés – 30 perc után figyelmeztetés, majd automatikus befejezés
  useEffect(() => {
    if (!activeWorkout || viewWorkoutId || isBackdateMode) return;

    const checkInterval = setInterval(() => {
      if (
        Date.now() - lastActivityRef.current >= INACTIVITY_TIMEOUT_MS &&
        !inactivityTriggeredRef.current
      ) {
        inactivityTriggeredRef.current = true;
        setShowInactivityModal(true);
        setInactivityCountdown(INACTIVITY_AUTO_END_SECONDS);
      }
    }, 30000);

    return () => clearInterval(checkInterval);
  }, [activeWorkout, viewWorkoutId, isBackdateMode]);

  const handleInactivityAutoEnd = useCallback(async () => {
    if (!activeWorkout) return;
    setShowInactivityModal(false);
    inactivityTriggeredRef.current = false;
    setEndingWorkout(true);
    try {
      if (activeExerciseId && activeExerciseStartTime) {
        const elapsed = getExerciseElapsedSeconds(activeExerciseId);
        if (elapsed > 0) {
          await exerciseApi.updateTime(activeExerciseId, elapsed).catch(() => {});
        }
      }
      await endWorkout({ endMood: 4, notes: 'Automatikusan befejezve inaktivitás miatt' });
      showToast('Edzés automatikusan befejezve (inaktivitás)', 'success');
      navigate('/history');
    } catch (error) {
      console.error('Failed to auto-end workout:', error);
      showToast('Hiba az automatikus befejezéskor.', 'error');
    } finally {
      setEndingWorkout(false);
    }
  }, [activeWorkout, activeExerciseId, activeExerciseStartTime, getExerciseElapsedSeconds, endWorkout, showToast, navigate]);

  useEffect(() => {
    if (!showInactivityModal) return;

    const countdown = setInterval(() => {
      setInactivityCountdown((c) => {
        if (c <= 1) {
          handleInactivityAutoEnd();
          return 0;
        }
        return c - 1;
      });
    }, 1000);

    return () => clearInterval(countdown);
  }, [showInactivityModal, handleInactivityAutoEnd]);

  const closeExerciseSwapModal = () => {
    setShowViewExerciseSwapModal(false);
    setViewSwapExerciseId(null);
    setExerciseSwapContext(null);
  };

  // Filter machines client-side for instant results without loading
  const filteredMachines = useMemo(() => {
    return machines.filter((machine) => {
      const matchesSearch = !machineSearchQuery || 
        machine.name.toLowerCase().includes(machineSearchQuery.toLowerCase());
      const matchesType = machineMuscleGroupMatches(machine.type, machineSelectedType);
      const matchesFloorPlan = !machineSelectedFloorPlanId ||
        machine.floorPlanPosition?.floorPlanId === machineSelectedFloorPlanId;
      return matchesSearch && matchesType && matchesFloorPlan;
    });
  }, [machines, machineSearchQuery, machineSelectedType, machineSelectedFloorPlanId]);

  const renderMachineSortHint = () => (
    <p className="text-xs text-gray-500 dark:text-gray-400 mb-3 px-2 py-1.5 bg-gray-50 dark:bg-gray-800/60 rounded-md border border-gray-100 dark:border-gray-700">
      Alapértelmezett rendezés: legtöbbször használt gépek
    </p>
  );

  const activeFloorPlans = useMemo(
    () => floorPlans.filter((fp) => fp.isActive),
    [floorPlans]
  );

  const renderFloorPlanFilter = () => (
    activeFloorPlans.length > 0 ? (
      <div className="mb-3">
        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
          Terem alaprajz
        </label>
        <div className="relative">
          <select
            className="w-full px-2 py-1.5 pr-8 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 appearance-none"
            value={machineSelectedFloorPlanId}
            onChange={(e) => setMachineSelectedFloorPlanId(e.target.value)}
          >
            <option value="">Összes alaprajz</option>
            {activeFloorPlans.map((fp) => (
              <option key={fp.id} value={fp.id}>{fp.name}</option>
            ))}
          </select>
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        </div>
      </div>
    ) : null
  );

  const renderExerciseSwapModal = () => (
    <Modal
      isOpen={showViewExerciseSwapModal}
      onClose={closeExerciseSwapModal}
      title="Gyakorlat cseréje"
      size="xl"
    >
      <div className="flex border-b border-gray-200 mb-4 -mt-2">
        <button
          type="button"
          className={`flex-1 py-2 px-3 text-sm font-medium border-b-2 transition-colors flex items-center justify-center gap-2 ${
            exerciseTabType === 'equipment'
              ? 'border-primary-500 text-primary-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => { setExerciseTabType('equipment'); setBodyweightSubcategory(''); }}
        >
          <Dumbbell className="w-4 h-4" />
          Gépek
        </button>
        <button
          type="button"
          className={`flex-1 py-2 px-3 text-sm font-medium border-b-2 transition-colors flex items-center justify-center gap-2 ${
            exerciseTabType === 'bodyweight'
              ? 'border-primary-500 text-primary-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => { setExerciseTabType('bodyweight'); setMachineSelectedType(''); }}
        >
          <User className="w-4 h-4" />
          Saját súlyos
        </button>
      </div>

      <div className="relative mb-3">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          className="w-full pl-8 pr-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          placeholder="Keresés..."
          value={machineSearchQuery}
          onChange={(e) => setMachineSearchQuery(e.target.value)}
        />
      </div>

      {exerciseTabType === 'equipment' && (
        <>
          {renderMachineSortHint()}

          <div className="relative mb-3">
            <select
              className="w-full px-2 py-1.5 pr-8 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 appearance-none"
              value={machineSelectedType}
              onChange={(e) => setMachineSelectedType(e.target.value)}
            >
              <option value="">Minden típus</option>
              {machineTypes.map((type) => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>

          {renderFloorPlanFilter()}

          {loadingMachines ? (
            <div className="text-center py-8">
              <div className="inline-block w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filteredMachines.length === 0 ? (
            <p className="text-gray-600 dark:text-gray-400 py-4 text-center">Nincs találat.</p>
          ) : (
            <div className="space-y-3 max-h-[50vh] overflow-y-auto">
              {filteredMachines.map((machine) => {
                const fp = machine.floorPlanPosition?.floorPlanId
                  ? floorPlans.find(f => f.id === machine.floorPlanPosition?.floorPlanId) || null
                  : null;
                return (
                  <MachineCard
                    key={machine.id}
                    machine={machine}
                    floorPlan={fp}
                    onClick={() => handleSwapToMachine(machine)}
                  />
                );
              })}
            </div>
          )}
        </>
      )}

      {exerciseTabType === 'bodyweight' && (
        <>
          <div className="flex flex-wrap gap-2 mb-3">
            <button type="button" className={`px-3 py-1.5 text-sm rounded-full transition-all ${bodyweightSubcategory === '' ? 'bg-primary-100 text-primary-700 font-medium' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300'}`} onClick={() => setBodyweightSubcategory('')}>Mind</button>
            {[
              { id: 'upper' as const, label: 'Felsőtest', icon: '💪' },
              { id: 'lower' as const, label: 'Alsótest', icon: '🦵' },
              { id: 'core' as const, label: 'Törzs', icon: '🎯' },
              { id: 'complex' as const, label: 'Komplex', icon: '🔥' },
            ].map((sub) => (
              <button key={sub.id} type="button" className={`px-3 py-1.5 text-sm rounded-full transition-all flex items-center gap-1 ${bodyweightSubcategory === sub.id ? 'bg-primary-100 text-primary-700 font-medium' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300'}`} onClick={() => setBodyweightSubcategory(sub.id)}>
                <span>{sub.icon}</span><span>{sub.label}</span>
              </button>
            ))}
          </div>
          {loadingMachines ? (
            <div className="text-center py-8">
              <div className="inline-block w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filteredBodyweight.length === 0 ? (
            <p className="text-gray-600 dark:text-gray-400 py-4 text-center">Nincs találat.</p>
          ) : (
            <div className="space-y-2 max-h-[50vh] overflow-y-auto">
              {filteredBodyweight.map((bwExercise) => (
                <button
                  key={bwExercise.id}
                  type="button"
                  onClick={() => handleSwapToBodyweight(bwExercise)}
                  className="w-full text-left p-3 bg-gray-50 dark:bg-gray-700 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors border border-transparent hover:border-primary-200"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900 dark:text-gray-100">{bwExercise.name}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{bwExercise.muscleGroup}</p>
                    </div>
                    <Plus className="w-5 h-5 text-gray-400" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </Modal>
  );

  // Filter bodyweight exercises
  const filteredBodyweight = useMemo(() => {
    return bodyweightExercises.filter((exercise) => {
      const matchesSearch = !machineSearchQuery || 
        exercise.name.toLowerCase().includes(machineSearchQuery.toLowerCase());
      const matchesSubcategory = !bodyweightSubcategory || exercise.subcategory === bodyweightSubcategory;
      return matchesSearch && matchesSubcategory;
    });
  }, [bodyweightExercises, machineSearchQuery, bodyweightSubcategory]);

  // Bodyweight subcategory options
  const bodyweightSubcategories = [
    { id: 'upper' as const, label: 'Felsőtest', icon: '💪' },
    { id: 'lower' as const, label: 'Alsótest', icon: '🦵' },
    { id: 'core' as const, label: 'Törzs', icon: '🎯' },
    { id: 'complex' as const, label: 'Komplex', icon: '🔥' },
  ];

  // Determine if selected exercise is bodyweight and get its name
  const selectedExerciseInfo = useMemo(() => {
    if (!selectedExerciseId || !activeWorkout) return { isBodyweight: false, name: '' };
    const exercise = activeWorkout.exercises.find(e => e.id === selectedExerciseId);
    const isBodyweight = !!exercise?.bodyweightExercise;
    const name = exercise?.bodyweightExercise?.name || exercise?.machine?.name || '';
    return { isBodyweight, name };
  }, [selectedExerciseId, activeWorkout]);

  const isBodyweightExercise = selectedExerciseInfo.isBodyweight;

  const handleSelectMachine = async (machine: Machine) => {
    try {
      await addExercise({ machineId: machine.id });
      recordActivity();
      showToast(`${machine.name} hozzáadva!`, 'success');
      setShowMachineModal(false);
    } catch (error) {
      console.error('Failed to add exercise:', error);
      showToast('Hiba a gyakorlat hozzáadásakor.', 'error');
    }
  };

  const handleSelectBodyweightExercise = async (exercise: BodyweightExercise) => {
    try {
      await addExercise({ bodyweightExerciseId: exercise.id });
      recordActivity();
      showToast(`${exercise.name} hozzáadva!`, 'success');
      setShowMachineModal(false);
    } catch (error) {
      console.error('Failed to add exercise:', error);
      showToast('Hiba a gyakorlat hozzáadásakor.', 'error');
    }
  };

  const handleOpenActiveExerciseSwapModal = (exerciseId: string) => {
    setViewSwapExerciseId(exerciseId);
    setExerciseSwapContext('active');
    loadMachines();
    setMachineSearchQuery('');
    setMachineSelectedType('');
    setMachineSelectedFloorPlanId('');
    setExerciseTabType('equipment');
    setBodyweightSubcategory('');
    setShowViewExerciseSwapModal(true);
    recordActivity();
  };

  const handleActiveSwapToMachine = async (machine: Machine) => {
    if (!viewSwapExerciseId) return;
    try {
      await updateExerciseInWorkout(viewSwapExerciseId, { machineId: machine.id });
      recordActivity();
      showToast(`Gyakorlat lecserélve: ${machine.name}`, 'success');
      closeExerciseSwapModal();
    } catch (error) {
      console.error('Failed to swap exercise:', error);
      showToast('Hiba a gyakorlat cseréjekor.', 'error');
    }
  };

  const handleActiveSwapToBodyweight = async (bwExercise: BodyweightExercise) => {
    if (!viewSwapExerciseId) return;
    try {
      await updateExerciseInWorkout(viewSwapExerciseId, { bodyweightExerciseId: bwExercise.id });
      recordActivity();
      showToast(`Gyakorlat lecserélve: ${bwExercise.name}`, 'success');
      closeExerciseSwapModal();
    } catch (error) {
      console.error('Failed to swap exercise:', error);
      showToast('Hiba a gyakorlat cseréjekor.', 'error');
    }
  };

  const handleOpenDeleteExerciseModal = (exerciseId: string) => {
    const exercise = activeWorkout?.exercises.find((e) => e.id === exerciseId);
    const name = exercise?.machine?.name || exercise?.bodyweightExercise?.name || 'Gyakorlat';
    setDeleteExerciseId(exerciseId);
    setDeleteExerciseName(name);
    setShowDeleteExerciseModal(true);
    recordActivity();
  };

  const handleConfirmDeleteExercise = async () => {
    if (!deleteExerciseId) return;
    setDeletingExercise(true);
    try {
      if (activeExerciseId === deleteExerciseId) {
        setActiveExerciseId(null);
        setActiveExerciseStartTime(null);
      }
      await deleteExercise(deleteExerciseId);
      recordActivity();
      showToast('Gyakorlat törölve', 'success');
      setShowDeleteExerciseModal(false);
      setDeleteExerciseId(null);
    } catch (error) {
      console.error('Failed to delete exercise:', error);
      showToast('Hiba a gyakorlat törlésekor.', 'error');
    } finally {
      setDeletingExercise(false);
    }
  };

  // Clone an existing set (add a new set with the same values)
  const handleCloneSet = async (set: ExerciseSet, exerciseId: string) => {
    try {
      let setData: Parameters<typeof addSet>[1];
      
      if (set.setType === 'cardio') {
        setData = {
          setType: 'cardio',
          durationSeconds: set.durationSeconds || 0,
          resistanceLevel: set.resistanceLevel || undefined,
          difficulty: set.difficulty,
        };
      } else if (set.setType === 'dualHand') {
        setData = {
          setType: 'dualHand',
          leftReps: set.leftReps || 0,
          rightReps: set.rightReps || 0,
          leftWeight: set.leftWeight || 0,
          rightWeight: set.rightWeight || 0,
          difficulty: set.difficulty,
        };
      } else {
        setData = {
          setType: 'standard',
          reps: set.reps || 0,
          weight: set.weight || 0,
          difficulty: set.difficulty,
        };
      }
      
      await addSet(exerciseId, setData);
      showToast('Sorozat klónozva!', 'success');
    } catch (error) {
      console.error('Failed to clone set:', error);
      showToast('Hiba a sorozat klónozásakor.', 'error');
    }
  };

  const handleOpenSetModal = (exerciseId: string) => {
    setSelectedExerciseId(exerciseId);
    setSetType('standard');
    // Standard fields
    setReps(localStorage.getItem('defaultReps') || '12');
    setWeight('');
    // Cardio fields
    setDurationMinutes('5');
    setDurationSeconds('0');
    setResistanceLevel('');
    // Dual hand fields
    setLeftReps(localStorage.getItem('defaultReps') || '12');
    setRightReps(localStorage.getItem('defaultReps') || '12');
    setLeftWeight('');
    setRightWeight('');
    // Common
    setDifficulty(3);
    setShowSetModal(true);
  };

  const handleAddSet = async (e: FormEvent) => {
    e.preventDefault();
    if (!selectedExerciseId) return;

    setSavingSet(true);
    try {
      let setData: Parameters<typeof addSet>[1];
      
      if (setType === 'standard') {
        setData = {
          setType: 'standard',
          reps: parseInt(reps, 10),
          weight: parseFloat(weight) || 0,
          difficulty,
        };
      } else if (setType === 'cardio') {
        setData = {
          setType: 'cardio',
          durationSeconds: parseInt(durationMinutes, 10) * 60 + parseInt(durationSeconds, 10),
          resistanceLevel: resistanceLevel ? parseInt(resistanceLevel, 10) : undefined,
          difficulty,
        };
      } else {
        setData = {
          setType: 'dualHand',
          leftReps: parseInt(leftReps, 10),
          rightReps: parseInt(rightReps, 10),
          leftWeight: parseFloat(leftWeight) || 0,
          rightWeight: parseFloat(rightWeight) || 0,
          difficulty,
        };
      }
      
      await addSet(selectedExerciseId, setData);
      recordActivity();
      showToast('Sorozat rögzítve!', 'success');
      setShowSetModal(false);
    } catch (error) {
      console.error('Failed to add set:', error);
      showToast('Hiba a sorozat rögzítésekor.', 'error');
    } finally {
      setSavingSet(false);
    }
  };

  const handleOpenEditSetModal = (set: ExerciseSet, exerciseId: string) => {
    setEditingSetId(set.id);
    setEditingExerciseId(exerciseId);
    setSetType(set.setType || 'standard');
    
    // Standard fields
    setReps(set.reps?.toString() || '');
    setWeight(set.weight?.toString() || '');
    
    // Cardio fields
    if (set.durationSeconds) {
      setDurationMinutes(Math.floor(set.durationSeconds / 60).toString());
      setDurationSeconds((set.durationSeconds % 60).toString());
    } else {
      setDurationMinutes('5');
      setDurationSeconds('0');
    }
    setResistanceLevel(set.resistanceLevel?.toString() || '');
    
    // Dual hand fields
    setLeftReps(set.leftReps?.toString() || '');
    setRightReps(set.rightReps?.toString() || '');
    setLeftWeight(set.leftWeight?.toString() || '');
    setRightWeight(set.rightWeight?.toString() || '');
    
    setDifficulty(set.difficulty);
    setShowEditSetModal(true);
  };

  const handleEditSet = async (e: FormEvent) => {
    e.preventDefault();
    if (!editingSetId) return;

    setSavingSet(true);
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let updateData: any = { difficulty };
      
      if (setType === 'standard') {
        updateData.reps = parseInt(reps, 10);
        updateData.weight = parseFloat(weight) || 0;
      } else if (setType === 'cardio') {
        updateData.durationSeconds = parseInt(durationMinutes, 10) * 60 + parseInt(durationSeconds, 10);
        updateData.resistanceLevel = resistanceLevel ? parseInt(resistanceLevel, 10) : null;
      } else {
        updateData.leftReps = parseInt(leftReps, 10);
        updateData.rightReps = parseInt(rightReps, 10);
        updateData.leftWeight = parseFloat(leftWeight) || 0;
        updateData.rightWeight = parseFloat(rightWeight) || 0;
      }
      
      await updateSet(editingSetId, updateData);
      showToast('Sorozat módosítva!', 'success');
      setShowEditSetModal(false);
      setEditingSetId(null);
      setEditingExerciseId(null);
    } catch (error) {
      console.error('Failed to update set:', error);
      showToast('Hiba a sorozat módosításakor.', 'error');
    } finally {
      setSavingSet(false);
    }
  };

  const handleOpenDeleteSetModal = (setId: string, exerciseId: string) => {
    setDeletingSetId(setId);
    setDeletingExerciseId(exerciseId);
    setShowDeleteSetModal(true);
  };

  const handleConfirmDeleteSet = async () => {
    if (!deletingSetId || !deletingExerciseId) return;
    
    setDeletingSet(true);
    try {
      await deleteSet(deletingSetId, deletingExerciseId);
      showToast('Sorozat törölve!', 'success');
      setShowDeleteSetModal(false);
      setDeletingSetId(null);
      setDeletingExerciseId(null);
    } catch (error) {
      console.error('Failed to delete set:', error);
      showToast('Hiba a sorozat törlésekor.', 'error');
    } finally {
      setDeletingSet(false);
    }
  };

  // ======================================
  // VIEW MODE HANDLERS
  // ======================================

  // View mode: determine exercise info for set modal
  const viewSelectedExerciseInfo = useMemo(() => {
    if (!selectedExerciseId || !viewWorkout) return { isBodyweight: false, name: '' };
    const exercise = viewWorkout.exercises.find(e => e.id === selectedExerciseId);
    const isBodyweight = !!exercise?.bodyweightExercise;
    const name = exercise?.bodyweightExercise?.name || exercise?.machine?.name || '';
    return { isBodyweight, name };
  }, [selectedExerciseId, viewWorkout]);

  // View mode: open exercise swap modal
  const handleViewOpenExerciseSwapModal = (exerciseId: string) => {
    setViewSwapExerciseId(exerciseId);
    setExerciseSwapContext('view');
    loadMachines();
    setMachineSearchQuery('');
    setMachineSelectedType('');
    setMachineSelectedFloorPlanId('');
    setExerciseTabType('equipment');
    setBodyweightSubcategory('');
    setShowViewExerciseSwapModal(true);
  };

  const handleSwapToMachine = async (machine: Machine) => {
    if (exerciseSwapContext === 'active') {
      await handleActiveSwapToMachine(machine);
    } else {
      await handleViewSwapToMachine(machine);
    }
  };

  const handleSwapToBodyweight = async (bwExercise: BodyweightExercise) => {
    if (exerciseSwapContext === 'active') {
      await handleActiveSwapToBodyweight(bwExercise);
    } else {
      await handleViewSwapToBodyweight(bwExercise);
    }
  };

  // View mode: swap exercise to machine
  const handleViewSwapToMachine = async (machine: Machine) => {
    if (!viewSwapExerciseId || !viewWorkout) return;
    try {
      const response = await exerciseApi.updateExercise(viewSwapExerciseId, { machineId: machine.id });
      if (response.success && response.data) {
        setViewWorkout({
          ...viewWorkout,
          exercises: viewWorkout.exercises.map(ex =>
            ex.id === viewSwapExerciseId ? response.data!.exercise : ex
          ),
        });
        showToast(`Gyakorlat lecserélve: ${machine.name}`, 'success');
      }
      closeExerciseSwapModal();
    } catch (error) {
      console.error('Failed to swap exercise:', error);
      showToast('Hiba a gyakorlat cseréjekor.', 'error');
    }
  };

  // View mode: swap exercise to bodyweight
  const handleViewSwapToBodyweight = async (bwExercise: BodyweightExercise) => {
    if (!viewSwapExerciseId || !viewWorkout) return;
    try {
      const response = await exerciseApi.updateExercise(viewSwapExerciseId, { bodyweightExerciseId: bwExercise.id });
      if (response.success && response.data) {
        setViewWorkout({
          ...viewWorkout,
          exercises: viewWorkout.exercises.map(ex =>
            ex.id === viewSwapExerciseId ? response.data!.exercise : ex
          ),
        });
        showToast(`Gyakorlat lecserélve: ${bwExercise.name}`, 'success');
      }
      closeExerciseSwapModal();
    } catch (error) {
      console.error('Failed to swap exercise:', error);
      showToast('Hiba a gyakorlat cseréjekor.', 'error');
    }
  };

  // View mode: open add set modal
  const handleViewOpenAddSetModal = (exerciseId: string) => {
    setSelectedExerciseId(exerciseId);
    setViewSetFormMode('add');
    setSetType('standard');
    setReps(localStorage.getItem('defaultReps') || '12');
    setWeight('');
    setDurationMinutes('5');
    setDurationSeconds('0');
    setResistanceLevel('');
    setLeftReps(localStorage.getItem('defaultReps') || '12');
    setRightReps(localStorage.getItem('defaultReps') || '12');
    setLeftWeight('');
    setRightWeight('');
    setDifficulty(3);
    setShowViewSetFormModal(true);
  };

  // View mode: open edit set modal
  const handleViewOpenEditSetModal = (set: ExerciseSet, exerciseId: string) => {
    setSelectedExerciseId(exerciseId);
    setEditingSetId(set.id);
    setViewSetFormMode('edit');
    setSetType(set.setType || 'standard');
    setReps(set.reps?.toString() || '');
    setWeight(set.weight?.toString() || '');
    if (set.durationSeconds) {
      setDurationMinutes(Math.floor(set.durationSeconds / 60).toString());
      setDurationSeconds((set.durationSeconds % 60).toString());
    } else {
      setDurationMinutes('5');
      setDurationSeconds('0');
    }
    setResistanceLevel(set.resistanceLevel?.toString() || '');
    setLeftReps(set.leftReps?.toString() || '');
    setRightReps(set.rightReps?.toString() || '');
    setLeftWeight(set.leftWeight?.toString() || '');
    setRightWeight(set.rightWeight?.toString() || '');
    setDifficulty(set.difficulty);
    setShowViewSetFormModal(true);
  };

  // View mode: save set (add or edit)
  const handleViewSaveSet = async (e: FormEvent) => {
    e.preventDefault();
    if (!viewWorkout) return;

    setViewSavingSet(true);
    try {
      if (viewSetFormMode === 'add' && selectedExerciseId) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let setData: any;
        if (setType === 'standard') {
          setData = { setType: 'standard', reps: parseInt(reps, 10), weight: parseFloat(weight) || 0, difficulty };
        } else if (setType === 'cardio') {
          setData = { setType: 'cardio', durationSeconds: parseInt(durationMinutes, 10) * 60 + parseInt(durationSeconds, 10), resistanceLevel: resistanceLevel ? parseInt(resistanceLevel, 10) : undefined, difficulty };
        } else {
          setData = { setType: 'dualHand', leftReps: parseInt(leftReps, 10), rightReps: parseInt(rightReps, 10), leftWeight: parseFloat(leftWeight) || 0, rightWeight: parseFloat(rightWeight) || 0, difficulty };
        }
        const response = await exerciseApi.addSet(selectedExerciseId, setData);
        if (response.success && response.data) {
          setViewWorkout({
            ...viewWorkout,
            exercises: viewWorkout.exercises.map(ex =>
              ex.id === selectedExerciseId
                ? { ...ex, sets: [...ex.sets, response.data!.set] }
                : ex
            ),
          });
          showToast('Sorozat hozzáadva!', 'success');
        }
      } else if (viewSetFormMode === 'edit' && editingSetId) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let updateData: any = { difficulty };
        if (setType === 'standard') {
          updateData.reps = parseInt(reps, 10);
          updateData.weight = parseFloat(weight) || 0;
        } else if (setType === 'cardio') {
          updateData.durationSeconds = parseInt(durationMinutes, 10) * 60 + parseInt(durationSeconds, 10);
          updateData.resistanceLevel = resistanceLevel ? parseInt(resistanceLevel, 10) : null;
        } else {
          updateData.leftReps = parseInt(leftReps, 10);
          updateData.rightReps = parseInt(rightReps, 10);
          updateData.leftWeight = parseFloat(leftWeight) || 0;
          updateData.rightWeight = parseFloat(rightWeight) || 0;
        }
        const response = await exerciseApi.updateSet(editingSetId, updateData);
        if (response.success && response.data) {
          setViewWorkout({
            ...viewWorkout,
            exercises: viewWorkout.exercises.map(ex => ({
              ...ex,
              sets: ex.sets.map(s => s.id === editingSetId ? response.data!.set : s),
            })),
          });
          showToast('Sorozat módosítva!', 'success');
        }
      }
      setShowViewSetFormModal(false);
      setEditingSetId(null);
      setSelectedExerciseId(null);
    } catch (error) {
      console.error('Failed to save set:', error);
      showToast('Hiba a sorozat mentésekor.', 'error');
    } finally {
      setViewSavingSet(false);
    }
  };

  // View mode: open delete set confirm
  const handleViewOpenDeleteSetModal = (setId: string, exerciseId: string) => {
    setDeletingSetId(setId);
    setDeletingExerciseId(exerciseId);
    setShowViewDeleteConfirmModal(true);
  };

  // View mode: confirm delete set
  const handleViewConfirmDeleteSet = async () => {
    if (!deletingSetId || !deletingExerciseId || !viewWorkout) return;

    setViewDeletingSet(true);
    try {
      await exerciseApi.deleteSet(deletingSetId);
      setViewWorkout({
        ...viewWorkout,
        exercises: viewWorkout.exercises.map(ex =>
          ex.id === deletingExerciseId
            ? { ...ex, sets: ex.sets.filter(s => s.id !== deletingSetId) }
            : ex
        ),
      });
      showToast('Sorozat törölve!', 'success');
      setShowViewDeleteConfirmModal(false);
      setDeletingSetId(null);
      setDeletingExerciseId(null);
    } catch (error) {
      console.error('Failed to delete set:', error);
      showToast('Hiba a sorozat törlésekor.', 'error');
    } finally {
      setViewDeletingSet(false);
    }
  };

  if (isLoading || loadingView) {
    return (
      <div className="text-center py-12">
        <div className="inline-block w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // View mode - showing a past workout (with editing)
  if (viewWorkout) {
    const formatDuration = (start: string, end: string | null) => {
      if (!end) return 'Folyamatban';
      const startDate = new Date(start);
      const endDate = new Date(end);
      const durationMs = endDate.getTime() - startDate.getTime();
      const minutes = Math.floor(durationMs / 60000);
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      if (hours > 0) {
        return `${hours} óra ${remainingMinutes} perc`;
      }
      return `${minutes} perc`;
    };

    const formatDateTime = (date: string) => {
      return new Date(date).toLocaleString('hu-HU', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    };

    const viewIsBodyweightExercise = viewSelectedExerciseInfo.isBodyweight;

    return (
      <div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <Link
                to="/history"
                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </Link>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Edzés részletei</h1>
            </div>
            <p className="text-gray-600 text-sm sm:text-base">
              {formatDateTime(viewWorkout.startTime)}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-lg">
              <Clock className="w-4 h-4 text-gray-600" />
              <span className="font-semibold text-gray-700">
                {formatDuration(viewWorkout.startTime, viewWorkout.endTime)}
              </span>
            </div>
            {viewWorkout.bodyWeight && (
              <div className="px-3 py-1.5 bg-gray-100 rounded-lg">
                <span className="font-semibold text-gray-700">{viewWorkout.bodyWeight} kg</span>
              </div>
            )}
          </div>
        </div>

        {viewWorkout.exercises.length === 0 ? (
          <Card>
            <div className="text-center py-12">
              <p className="text-gray-600">Ez az edzés nem tartalmaz gyakorlatokat.</p>
            </div>
          </Card>
        ) : (
          <div className="space-y-4">
            {viewWorkout.exercises.map((exercise, index) => {
              const floorPlan = exercise.machine?.floorPlanPosition?.floorPlanId
                ? floorPlans.find(fp => fp.id === exercise.machine?.floorPlanPosition?.floorPlanId) || null
                : null;

              const exerciseNumber = index + 1;

              // Get elapsed time for this exercise
              const exerciseElapsed = (exercise as { elapsedSeconds?: number }).elapsedSeconds || 0;

              return (
                <Card key={exercise.id}>
                  {/* Machine info with elapsed time in header */}
                  <div className="mb-4 -mx-6 -mt-4">
                    <MachineCard
                      machine={exercise.machine}
                      bodyweightExercise={exercise.bodyweightExercise}
                      floorPlan={floorPlan}
                      exerciseNumber={exerciseNumber}
                      elapsedSeconds={exerciseElapsed}
                      exerciseNotes={{
                        exerciseId: exercise.id,
                        notes: viewExerciseNotesMap[exercise.id] ?? exercise.notes ?? null,
                        onNotesUpdate: handleViewExerciseNotesUpdate,
                      }}
                      onSwapExercise={() => handleViewOpenExerciseSwapModal(exercise.id)}
                    />
                  </div>

                  {/* Add set button */}
                  <div className="mb-4">
                    <Button
                      size="sm"
                      variant="outline"
                      icon={<Plus className="w-4 h-4" />}
                      onClick={() => handleViewOpenAddSetModal(exercise.id)}
                      className="w-full"
                    >
                      Sorozat hozzáadása
                    </Button>
                  </div>

                  {/* Sets table */}
                  {exercise.sets.length === 0 ? (
                    <p className="text-gray-600 text-center py-4">Nincs rögzített sorozat.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-gray-200">
                            <th className="text-left py-1 px-1 text-sm font-medium text-gray-600 w-8">#</th>
                            <th className="text-left py-1 px-1 text-sm font-medium text-gray-600 w-10"></th>
                            <th className="text-left py-1 px-1 text-sm font-medium text-gray-600">Részletek</th>
                            <th className="text-center py-2 px-1 text-sm font-medium text-gray-600 w-8"></th>
                            <th className="text-right py-1 px-1 text-sm font-medium text-gray-600 w-16"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {exercise.sets.map((set) => {
                            const setTypeLabel = set.setType === 'cardio' ? '🏃' : set.setType === 'dualHand' ? '🙌' : '💪';
                            const difficultyEmojis = ['', '😄', '🙂', '😐', '😓', '🥵'];
                            
                            // Dual hand - render two rows
                            if (set.setType === 'dualHand') {
                              return (
                                <React.Fragment key={set.id}>
                                  <tr className="border-b border-gray-50">
                                    <td className="py-1 px-1 text-gray-500 text-sm" rowSpan={2}>{set.setNumber}</td>
                                    <td className="py-1 px-1" rowSpan={2}>{setTypeLabel}</td>
                                    <td className="py-1 px-1 text-sm">
                                      <span className="inline-flex items-center gap-1">
                                        <span className="text-blue-600 font-medium">B:</span>
                                        <span>{set.leftReps || 0} db × {set.leftWeight || 0} kg</span>
                                      </span>
                                    </td>
                                    <td className="py-1 px-1 text-center text-lg" rowSpan={2} title={`Nehézség: ${set.difficulty}/5`}>
                                      {difficultyEmojis[set.difficulty] || '😐'}
                                    </td>
                                    <td className="py-1 px-1 text-right" rowSpan={2}>
                                      <div className="flex items-center justify-end gap-1">
                                        <button
                                          type="button"
                                          onClick={() => handleViewOpenEditSetModal(set, exercise.id)}
                                          className="p-1 hover:bg-gray-100 rounded text-gray-500 hover:text-primary-600"
                                          title="Szerkesztés"
                                        >
                                          <Pencil className="w-3.5 h-3.5" />
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => handleViewOpenDeleteSetModal(set.id, exercise.id)}
                                          className="p-1 hover:bg-gray-100 rounded text-gray-500 hover:text-red-600"
                                          title="Törlés"
                                        >
                                          <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                  <tr className="border-b border-gray-100">
                                    <td className="py-1 px-1 text-sm">
                                      <span className="inline-flex items-center gap-1">
                                        <span className="text-green-600 font-medium">J:</span>
                                        <span>{set.rightReps || 0} db × {set.rightWeight || 0} kg</span>
                                      </span>
                                    </td>
                                  </tr>
                                </React.Fragment>
                              );
                            }
                            
                            // Standard and cardio - single row
                            let detailsText = '';
                            if (set.setType === 'cardio' && set.durationSeconds) {
                              const mins = Math.floor(set.durationSeconds / 60);
                              const secs = set.durationSeconds % 60;
                              const levelText = set.resistanceLevel ? ` (fok: ${set.resistanceLevel})` : '';
                              detailsText = `${mins}:${secs.toString().padStart(2, '0')}${levelText}`;
                            } else {
                              detailsText = `${set.reps || 0} db × ${set.weight || 0} kg`;
                            }
                            
                            return (
                              <tr key={set.id} className="border-b border-gray-100">
                                <td className="py-1 px-1 text-gray-500 text-sm">{set.setNumber}</td>
                                <td className="py-1 px-1">{setTypeLabel}</td>
                                <td className="py-1 px-1 text-sm">{detailsText}</td>
                                <td className="py-2 px-1 text-center text-lg" title={`Nehézség: ${set.difficulty}/5`}>
                                  {difficultyEmojis[set.difficulty] || '😐'}
                                </td>
                                <td className="py-1 px-1 text-right">
                                  <div className="flex items-center justify-end gap-1">
                                    <button
                                      type="button"
                                      onClick={() => handleViewOpenEditSetModal(set, exercise.id)}
                                      className="p-1 hover:bg-gray-100 rounded text-gray-500 hover:text-primary-600"
                                      title="Szerkesztés"
                                    >
                                      <Pencil className="w-3.5 h-3.5" />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleViewOpenDeleteSetModal(set.id, exercise.id)}
                                      className="p-1 hover:bg-gray-100 rounded text-gray-500 hover:text-red-600"
                                      title="Törlés"
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}

        {/* Back button at bottom */}
        <div className="mt-8 text-center">
          <Link to="/history">
            <Button variant="outline" icon={<ArrowLeft className="w-4 h-4" />}>
              Vissza az előzményekhez
            </Button>
          </Link>
        </div>

        {/* ====== VIEW MODE MODALS ====== */}

        {renderExerciseSwapModal()}

        {/* View Mode: Set Add/Edit Modal */}
        <Modal
          isOpen={showViewSetFormModal}
          onClose={() => { setShowViewSetFormModal(false); setEditingSetId(null); setSelectedExerciseId(null); }}
          title={viewSetFormMode === 'add' ? 'Sorozat hozzáadása' : 'Sorozat szerkesztése'}
          footer={
            <>
              <button className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg" onClick={() => { setShowViewSetFormModal(false); setEditingSetId(null); setSelectedExerciseId(null); }}>Mégse</button>
              <Button type="submit" form="view-set-form" loading={viewSavingSet}>
                {viewSetFormMode === 'add' ? 'Hozzáadás' : 'Mentés'}
              </Button>
            </>
          }
        >
          {/* Exercise name badge */}
          {viewSelectedExerciseInfo.name && (
            <div className="-mt-2 mb-2 text-center">
              <span className="inline-block text-sm font-semibold text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 px-3 py-1 rounded-lg">
                {viewSelectedExerciseInfo.name}
              </span>
            </div>
          )}
          <form id="view-set-form" onSubmit={handleViewSaveSet}>
            {/* Set Type Tabs */}
            {viewIsBodyweightExercise ? (
              <div className="flex border-b border-gray-200 mb-4">
                <button type="button" className={`flex-1 py-2 px-3 text-sm font-medium border-b-2 transition-colors ${setType === 'standard' ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`} onClick={() => setSetType('standard')}>Ismétlés</button>
                <button type="button" className={`flex-1 py-2 px-3 text-sm font-medium border-b-2 transition-colors ${setType === 'cardio' ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`} onClick={() => setSetType('cardio')}>Időtartam</button>
              </div>
            ) : (
              <div className="flex border-b border-gray-200 mb-4">
                <button type="button" className={`flex-1 py-2 px-3 text-sm font-medium border-b-2 transition-colors ${setType === 'standard' ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`} onClick={() => setSetType('standard')}>Alap</button>
                <button type="button" className={`flex-1 py-2 px-3 text-sm font-medium border-b-2 transition-colors ${setType === 'cardio' ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`} onClick={() => setSetType('cardio')}>Kardió</button>
                <button type="button" className={`flex-1 py-2 px-3 text-sm font-medium border-b-2 transition-colors ${setType === 'dualHand' ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`} onClick={() => setSetType('dualHand')}>Két kéz</button>
              </div>
            )}

            {/* Standard Set Fields */}
            {setType === 'standard' && (
              <div className={viewIsBodyweightExercise ? 'space-y-4 mb-4' : 'grid grid-cols-2 gap-4 mb-4'}>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Ismétlések száma</label>
                  <input type="number" className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500" value={reps} onChange={(e) => setReps(e.target.value)} min="1" max="100" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Súly (kg){viewIsBodyweightExercise && <span className="text-gray-400 font-normal"> - opcionális</span>}</label>
                  <input type="number" className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500" value={weight} onChange={(e) => setWeight(e.target.value)} min="0" max="1000" step="0.5" />
                </div>
              </div>
            )}

            {/* Cardio Set Fields */}
            {setType === 'cardio' && (
              <div className="space-y-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Időtartam</label>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center">
                      <input type="number" className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500" value={durationMinutes} onChange={(e) => setDurationMinutes(e.target.value)} min="0" max="999" required />
                      <span className="ml-2 text-gray-600 dark:text-gray-400">perc</span>
                    </div>
                    <div className="flex items-center">
                      <input type="number" className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500" value={durationSeconds} onChange={(e) => setDurationSeconds(e.target.value)} min="0" max="59" required />
                      <span className="ml-2 text-gray-600 dark:text-gray-400">mp</span>
                    </div>
                  </div>
                </div>
                {!viewIsBodyweightExercise && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Gép fokozata (opcionális)</label>
                    <input type="number" className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500" value={resistanceLevel} onChange={(e) => setResistanceLevel(e.target.value)} min="1" max="30" placeholder="pl. 5" />
                  </div>
                )}
              </div>
            )}

            {/* Dual Hand Set Fields */}
            {setType === 'dualHand' && (
              <div className="space-y-4 mb-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
                    <p className="text-xs font-semibold text-blue-700 dark:text-blue-400 mb-2 text-center">Bal kéz</p>
                    <div className="space-y-2">
                      <div>
                        <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Ismétlés</label>
                        <input type="number" className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500" value={leftReps} onChange={(e) => setLeftReps(e.target.value)} min="0" max="100" required />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Súly (kg)</label>
                        <input type="number" className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500" value={leftWeight} onChange={(e) => setLeftWeight(e.target.value)} min="0" max="500" step="0.5" />
                      </div>
                    </div>
                  </div>
                  <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg">
                    <p className="text-xs font-semibold text-green-700 dark:text-green-400 mb-2 text-center">Jobb kéz</p>
                    <div className="space-y-2">
                      <div>
                        <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Ismétlés</label>
                        <input type="number" className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500" value={rightReps} onChange={(e) => setRightReps(e.target.value)} min="0" max="100" required />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Súly (kg)</label>
                        <input type="number" className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500" value={rightWeight} onChange={(e) => setRightWeight(e.target.value)} min="0" max="500" step="0.5" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Difficulty */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Nehézség (1-5)</label>
              <div className="grid grid-cols-5 gap-2">
                {[
                  { level: 1, emoji: '😄' },
                  { level: 2, emoji: '🙂' },
                  { level: 3, emoji: '😐' },
                  { level: 4, emoji: '😓' },
                  { level: 5, emoji: '🥵' },
                ].map(({ level, emoji }) => (
                  <button
                    key={level}
                    type="button"
                    className={`flex flex-col items-center justify-center py-2 px-1 rounded-lg font-semibold transition-all border-2 ${
                      difficulty === level
                        ? level === 1 ? 'bg-green-100 border-green-500 text-green-700'
                          : level === 2 ? 'bg-lime-100 border-lime-500 text-lime-700'
                          : level === 3 ? 'bg-yellow-100 border-yellow-500 text-yellow-700'
                          : level === 4 ? 'bg-orange-100 border-orange-500 text-orange-700'
                          : 'bg-red-100 border-red-500 text-red-700'
                        : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-400'
                    }`}
                    onClick={() => setDifficulty(level)}
                  >
                    <span className="text-xl mb-0.5">{emoji}</span>
                    <span className="text-sm">{level}</span>
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-500 text-center mt-2">1 = Könnyű, 5 = Nagyon nehéz</p>
            </div>
          </form>
        </Modal>

        {/* View Mode: Delete Set Confirm Modal */}
        <Modal
          isOpen={showViewDeleteConfirmModal}
          onClose={() => { setShowViewDeleteConfirmModal(false); setDeletingSetId(null); setDeletingExerciseId(null); }}
          title="Sorozat törlése"
          footer={
            <>
              <button className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg" onClick={() => { setShowViewDeleteConfirmModal(false); setDeletingSetId(null); setDeletingExerciseId(null); }}>Mégse</button>
              <Button variant="danger" loading={viewDeletingSet} onClick={handleViewConfirmDeleteSet}>Törlés</Button>
            </>
          }
        >
          <div className="text-center py-4">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Trash2 className="w-8 h-8 text-red-600" />
            </div>
            <p className="text-gray-700 dark:text-gray-300 text-lg mb-2">Biztosan törlöd ezt a sorozatot?</p>
            <p className="text-gray-500 text-sm">A törlés nem vonható vissza.</p>
          </div>
        </Modal>
      </div>
    );
  }

  if (!activeWorkout) {
    return (
      <div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div className="min-w-0">
            {isBackdateMode ? (
              <>
                <div className="flex items-center gap-2">
                  <Link
                    to="/history"
                    className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <ArrowLeft className="w-5 h-5 text-gray-600" />
                  </Link>
                  <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Korábbi edzés rögzítése</h1>
                </div>
                <p className="text-gray-600 dark:text-gray-400 mt-1 sm:mt-2 text-sm sm:text-base">Rögzíts egy korábban elvégzett edzést az előzményekbe</p>
              </>
            ) : (
              <>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Edzés</h1>
                <p className="text-gray-600 dark:text-gray-400 mt-1 sm:mt-2 text-sm sm:text-base">Indítsd el az edzésedet és kövesd nyomon a haladásodat</p>
              </>
            )}
          </div>
        </div>

        {/* Quick start card */}
        <Card>
          <div className="py-4">
            <div className="max-w-md mx-auto space-y-5">
              {/* Backdate date/time picker - only in backdate mode */}
              {isBackdateMode && (
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <CalendarIcon className="w-5 h-5 text-amber-600" />
                    <span className="font-medium text-amber-800 dark:text-amber-300">Edzés időpontja</span>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Dátum
                      </label>
                      <input
                        type="date"
                        value={backdateDate}
                        onChange={(e) => setBackdateDate(e.target.value)}
                        max={new Date().toISOString().split('T')[0]}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Kezdés ideje
                      </label>
                      <input
                        type="time"
                        value={backdateTime}
                        onChange={(e) => setBackdateTime(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Start mood */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {isBackdateMode ? 'Hogyan érezted magad az edzés előtt?' : 'Hogyan érzed magad?'}
                </label>
                <div className="grid grid-cols-5 gap-2">
                  {moodOptions.map(({ level, emoji, label }) => (
                    <button
                      key={level}
                      type="button"
                      className={`flex flex-col items-center justify-center py-2 px-1 rounded-lg font-semibold transition-all border-2 ${
                        startMood === level
                          ? level === 1 ? 'bg-red-100 border-red-500 text-red-700'
                            : level === 2 ? 'bg-orange-100 border-orange-500 text-orange-700'
                            : level === 3 ? 'bg-yellow-100 border-yellow-500 text-yellow-700'
                            : level === 4 ? 'bg-lime-100 border-lime-500 text-lime-700'
                            : 'bg-green-100 border-green-500 text-green-700'
                          : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-400'
                      }`}
                      onClick={() => setStartMood(level)}
                    >
                      <span className="text-xl mb-0.5">{emoji}</span>
                      <span className="text-xs">{label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Start button */}
              <div className="text-center">
                <Button
                  size="lg"
                  loading={startingWorkout}
                  onClick={handleStartWorkout}
                  icon={<Play className="w-5 h-5" />}
                  className="w-full"
                >
                  {isBackdateMode ? 'Edzés rögzítésének indítása' : 'Edzés indítása'}
                </Button>
              </div>
            </div>
          </div>
        </Card>

        {/* Calendar - scheduled workouts will appear here */}
        <div className="mt-6">
          <Calendar />
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {isBackdateMode ? 'Korábbi edzés rögzítése' : 'Aktív edzés'}
          </h1>
          {isBackdateMode ? (
            <div className="flex items-center gap-2 mt-1 sm:mt-2">
              <CalendarIcon className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600" />
              <span className="text-sm sm:text-base text-amber-700 dark:text-amber-400 font-medium">
                {activeWorkout && new Date(activeWorkout.startTime).toLocaleDateString('hu-HU', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
            </div>
          ) : (
            <div className="flex items-center gap-2 mt-1 sm:mt-2">
              <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-primary-600" />
              <span className="text-sm sm:text-base text-gray-600 dark:text-gray-400">Teljes edzés ideje:</span>
              <span className="text-lg sm:text-xl font-mono font-semibold text-primary-600">
                {elapsedTime}
              </span>
            </div>
          )}
        </div>
        <div className="flex flex-shrink-0 flex-1 justify-between">
          <Button
            variant="outline"
            size="sm"
            icon={<Plus className="w-4 h-4" />}
            onClick={handleOpenMachineModal}
            className="whitespace-nowrap"
          >
            Gyakorlat
          </Button>
          <Button
            variant="outline"
            size="sm"
            icon={<Square className="w-4 h-4 text-red-500" />}
            onClick={handleOpenConfirmEndModal}
            className="whitespace-nowrap"
          >
            Edzés befejezése
          </Button>
        </div>
      </div>

      {/* Calendar – aktív edzés alatt alapból összecsukva */}
      <div className="mb-6">
        <button
          type="button"
          onClick={() => setActiveWorkoutCalendarOpen((open) => !open)}
          className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          aria-expanded={activeWorkoutCalendarOpen}
        >
          <span className="flex items-center gap-2 min-w-0 flex-1">
            <CalendarIcon className="w-4 h-4 text-primary-600 flex-shrink-0" />
            <span>Naptár</span>
            {!activeWorkoutCalendarOpen && (
              <span className="text-gray-500 dark:text-gray-400 font-normal truncate">
                · Ma, {new Date().toLocaleDateString('hu-HU', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </span>
            )}
          </span>
          <ChevronDown
            className={`w-4 h-4 text-gray-500 flex-shrink-0 transition-transform ${activeWorkoutCalendarOpen ? 'rotate-180' : ''}`}
          />
        </button>
        {activeWorkoutCalendarOpen && (
          <div className="mt-2">
            <Calendar />
          </div>
        )}
      </div>

      {activeWorkout.exercises.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <p className="text-gray-600 mb-4">
              Még nincs gyakorlat. Adj hozzá egyet a "Gyakorlat" gombbal!
            </p>
            <Button
              variant="outline"
              icon={<Plus className="w-5 h-5" />}
              onClick={handleOpenMachineModal}
            >
              Első gyakorlat hozzáadása
            </Button>
          </div>
        </Card>
      ) : (
        <div className="space-y-4">
          {[...activeWorkout.exercises].reverse().map((exercise, index) => {
            // Find the floor plan for this machine
            const floorPlan = exercise.machine?.floorPlanPosition?.floorPlanId
              ? floorPlans.find(fp => fp.id === exercise.machine?.floorPlanPosition?.floorPlanId) || null
              : null;

            const exerciseNumber = activeWorkout.exercises.length - index;
            const isActive = activeExerciseId === exercise.id;

            return (
              <Card 
                key={exercise.id}
                className={`transition-all ${!isBackdateMode && isActive ? 'ring-2 ring-primary-500 shadow-lg' : ''}`}
              >
                {/* Machine info with photo and map */}
                <div className="mb-4 -mx-6 -mt-4">
                  <MachineCard
                    machine={exercise.machine}
                    bodyweightExercise={exercise.bodyweightExercise}
                    floorPlan={floorPlan}
                    exerciseNumber={exerciseNumber}
                    exerciseTimer={isBackdateMode ? undefined : {
                      isActive,
                      elapsedSeconds: getExerciseElapsedSeconds(exercise.id),
                      onStart: () => startExerciseTimer(exercise.id),
                      onStop: stopExerciseTimer,
                    }}
                    exerciseNotes={{
                      exerciseId: exercise.id,
                      notes: exerciseNotesMap[exercise.id] ?? exercise.notes ?? null,
                      onNotesUpdate: handleExerciseNotesUpdate,
                    }}
                    onSwapExercise={() => handleOpenActiveExerciseSwapModal(exercise.id)}
                    onDeleteExercise={() => handleOpenDeleteExerciseModal(exercise.id)}
                  />
                </div>

                {/* AI suggestion */}
                {aiSuggestions[exercise.id] && (
                  <div className="mb-3 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                    <p className="text-xs font-semibold text-amber-800 dark:text-amber-200 mb-2 flex items-center gap-1.5">
                      <Dumbbell className="w-3.5 h-3.5" />
                      AI javaslat
                    </p>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b border-amber-200 dark:border-amber-700">
                            <th className="text-left py-1 text-amber-700 dark:text-amber-300 w-8">#</th>
                            <th className="text-left py-1 text-amber-700 dark:text-amber-300">Ism.</th>
                            <th className="text-left py-1 text-amber-700 dark:text-amber-300">Súly</th>
                            <th className="text-left py-1 text-amber-700 dark:text-amber-300">Pihenő</th>
                          </tr>
                        </thead>
                        <tbody>
                          {aiSuggestions[exercise.id].sets.map((s, si) => (
                            <tr key={si} className="border-b border-amber-100 dark:border-amber-800/50">
                              <td className="py-1 text-amber-600 dark:text-amber-400">{si + 1}</td>
                              <td className="py-1 text-amber-900 dark:text-amber-100">
                                {s.reps != null ? `${s.reps} db` : s.durationSeconds != null ? `${s.durationSeconds}s` : '-'}
                              </td>
                              <td className="py-1 text-amber-900 dark:text-amber-100">
                                {s.weight != null ? `${s.weight} kg` : '-'}
                              </td>
                              <td className="py-1 text-amber-900 dark:text-amber-100">
                                {s.restSeconds != null ? `${s.restSeconds} mp` : '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {aiSuggestions[exercise.id].reasoning && (
                      <p className="mt-2 text-xs text-amber-700 dark:text-amber-300 italic">
                        {aiSuggestions[exercise.id].reasoning}
                      </p>
                    )}
                  </div>
                )}

                {/* Add set button */}
                <div className="mb-4">
                  <Button
                    size="sm"
                    icon={<Plus className="w-4 h-4" />}
                    onClick={() => handleOpenSetModal(exercise.id)}
                    className="w-full"
                  >
                    Sorozat hozzáadása
                  </Button>
                </div>

                {/* Sets table */}
                {exercise.sets.length === 0 ? (
                  <p className="text-gray-600 text-center py-4">Még nincs rögzített sorozat.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-1 px-1 text-sm font-medium text-gray-600 w-8">#</th>
                          <th className="text-left py-1 px-1 text-sm font-medium text-gray-600 w-10"></th>
                          <th className="text-left py-1 px-1 text-sm font-medium text-gray-600">Részletek</th>
                          <th className="text-center py-2 px-1 text-sm font-medium text-gray-600 w-8"></th>
                          <th className="text-right py-1 px-1 text-sm font-medium text-gray-600 w-16"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {exercise.sets.map((set) => {
                          const setTypeLabel = set.setType === 'cardio' ? '🏃' : set.setType === 'dualHand' ? '🙌' : '💪';
                          const difficultyEmojis = ['', '😄', '🙂', '😐', '😓', '🥵'];
                          
                          // Dual hand - render two rows
                          if (set.setType === 'dualHand') {
                            return (
                              <React.Fragment key={set.id}>
                                <tr className="border-b border-gray-50">
                                  <td className="py-1 px-1 text-gray-500 text-sm" rowSpan={2}>{set.setNumber}</td>
                                  <td className="py-1 px-1" rowSpan={2}>{setTypeLabel}</td>
                                  <td className="py-1 px-1 text-sm">
                                    <span className="inline-flex items-center gap-1">
                                      <span className="text-blue-600 font-medium">B:</span>
                                      <span>{set.leftReps || 0} db × {set.leftWeight || 0} kg</span>
                                    </span>
                                  </td>
                                  <td className="py-1 px-1 text-center text-lg" rowSpan={2} title={`Nehézség: ${set.difficulty}/5`}>
                                    {difficultyEmojis[set.difficulty] || '😐'}
                                  </td>
                                  <td className="py-1 px-1 text-right" rowSpan={2}>
                                    <div className="flex items-center justify-end gap-1">
                                      <button
                                        type="button"
                                        onClick={() => handleOpenEditSetModal(set, exercise.id)}
                                        className="p-1 hover:bg-gray-100 rounded text-gray-500 hover:text-primary-600"
                                        title="Szerkesztés"
                                      >
                                        <Pencil className="w-3.5 h-3.5" />
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => handleCloneSet(set, exercise.id)}
                                        className="p-1 hover:bg-gray-100 rounded text-gray-500 hover:text-green-600"
                                        title="Klónozás"
                                      >
                                        <Copy className="w-3.5 h-3.5" />
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => handleOpenDeleteSetModal(set.id, exercise.id)}
                                        className="p-1 hover:bg-gray-100 rounded text-gray-500 hover:text-red-600"
                                        title="Törlés"
                                      >
                                        <Trash2 className="w-3.5 h-3.5" />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                                <tr className="border-b border-gray-100">
                                  <td className="py-1 px-1 text-sm">
                                    <span className="inline-flex items-center gap-1">
                                      <span className="text-green-600 font-medium">J:</span>
                                      <span>{set.rightReps || 0} db × {set.rightWeight || 0} kg</span>
                                    </span>
                                  </td>
                                </tr>
                              </React.Fragment>
                            );
                          }
                          
                          // Standard and cardio - single row
                          let detailsText = '';
                          if (set.setType === 'cardio' && set.durationSeconds) {
                            const mins = Math.floor(set.durationSeconds / 60);
                            const secs = set.durationSeconds % 60;
                            const levelText = set.resistanceLevel ? ` (fok: ${set.resistanceLevel})` : '';
                            detailsText = `${mins}:${secs.toString().padStart(2, '0')}${levelText}`;
                          } else {
                            detailsText = `${set.reps || 0} db × ${set.weight || 0} kg`;
                          }
                          
                          return (
                            <tr key={set.id} className="border-b border-gray-100">
                              <td className="py-1 px-1 text-gray-500 text-sm">{set.setNumber}</td>
                              <td className="py-1 px-1">{setTypeLabel}</td>
                              <td className="py-1 px-1 text-sm">{detailsText}</td>
                              <td className="py-2 px-1 text-center text-lg" title={`Nehézség: ${set.difficulty}/5`}>
                                {difficultyEmojis[set.difficulty] || '😐'}
                              </td>
                              <td className="py-1 px-1 text-right">
                                <div className="flex items-center justify-end gap-1">
                                  <button
                                    type="button"
                                    onClick={() => handleOpenEditSetModal(set, exercise.id)}
                                    className="p-1 hover:bg-gray-100 rounded text-gray-500 hover:text-primary-600"
                                    title="Szerkesztés"
                                  >
                                    <Pencil className="w-3.5 h-3.5" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleCloneSet(set, exercise.id)}
                                    className="p-1 hover:bg-gray-100 rounded text-gray-500 hover:text-green-600"
                                    title="Klónozás"
                                  >
                                    <Copy className="w-3.5 h-3.5" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleOpenDeleteSetModal(set.id, exercise.id)}
                                    className="p-1 hover:bg-gray-100 rounded text-gray-500 hover:text-red-600"
                                    title="Törlés"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {renderExerciseSwapModal()}

      {/* Machine Selection Modal */}
      <Modal
        isOpen={showMachineModal}
        onClose={() => setShowMachineModal(false)}
        title="Gyakorlat hozzáadása"
        size="xl"
      >
        {/* Tab Selection */}
        <div className="flex border-b border-gray-200 mb-4 -mt-2">
          <button
            type="button"
            className={`flex-1 py-2 px-3 text-sm font-medium border-b-2 transition-colors flex items-center justify-center gap-2 ${
              exerciseTabType === 'equipment'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => {
              setExerciseTabType('equipment');
              setBodyweightSubcategory('');
            }}
          >
            <Dumbbell className="w-4 h-4" />
            Gépek
          </button>
          <button
            type="button"
            className={`flex-1 py-2 px-3 text-sm font-medium border-b-2 transition-colors flex items-center justify-center gap-2 ${
              exerciseTabType === 'bodyweight'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => {
              setExerciseTabType('bodyweight');
              setMachineSelectedType('');
            }}
          >
            <User className="w-4 h-4" />
            Saját súlyos
          </button>
        </div>

        {/* Search - common for both tabs */}
        <div className="relative mb-3">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            className="w-full pl-8 pr-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="Keresés..."
            value={machineSearchQuery}
            onChange={(e) => setMachineSearchQuery(e.target.value)}
          />
        </div>

        {/* Equipment Tab */}
        {exerciseTabType === 'equipment' && (
          <>
            {renderMachineSortHint()}

            {/* Type filter */}
            <div className="relative mb-3">
              <select
                className="w-full px-2 py-1.5 pr-8 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 appearance-none"
                value={machineSelectedType}
                onChange={(e) => setMachineSelectedType(e.target.value)}
              >
                <option value="">Minden típus</option>
                {machineTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>

            {renderFloorPlanFilter()}

            {loadingMachines ? (
              <div className="text-center py-8">
                <div className="inline-block w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : filteredMachines.length === 0 ? (
              <p className="text-gray-600 dark:text-gray-400 py-4 text-center">
                {machines.length === 0
                  ? 'Nincs még gép. Először adj hozzá gépeket a Gépek menüpontban.'
                  : 'Nincs találat a keresési feltételeknek.'}
              </p>
            ) : (
              <div className="space-y-3 max-h-[50vh] overflow-y-auto">
                {filteredMachines.map((machine) => {
                  const floorPlan = machine.floorPlanPosition?.floorPlanId
                    ? floorPlans.find(fp => fp.id === machine.floorPlanPosition?.floorPlanId) || null
                    : null;

                  return (
                    <MachineCard
                      key={machine.id}
                      machine={machine}
                      floorPlan={floorPlan}
                      onClick={() => handleSelectMachine(machine)}
                    />
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* Bodyweight Tab */}
        {exerciseTabType === 'bodyweight' && (
          <>
            {/* Subcategory filter chips */}
            <div className="flex flex-wrap gap-2 mb-3">
              <button
                type="button"
                className={`px-3 py-1.5 text-sm rounded-full transition-all ${
                  bodyweightSubcategory === ''
                    ? 'bg-primary-100 text-primary-700 font-medium'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300'
                }`}
                onClick={() => setBodyweightSubcategory('')}
              >
                Mind
              </button>
              {bodyweightSubcategories.map((sub) => (
                <button
                  key={sub.id}
                  type="button"
                  className={`px-3 py-1.5 text-sm rounded-full transition-all flex items-center gap-1 ${
                    bodyweightSubcategory === sub.id
                      ? 'bg-primary-100 text-primary-700 font-medium'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300'
                  }`}
                  onClick={() => setBodyweightSubcategory(sub.id)}
                >
                  <span>{sub.icon}</span>
                  <span>{sub.label}</span>
                </button>
              ))}
            </div>

            {loadingMachines ? (
              <div className="text-center py-8">
                <div className="inline-block w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : filteredBodyweight.length === 0 ? (
              <p className="text-gray-600 dark:text-gray-400 py-4 text-center">
                Nincs találat a keresési feltételeknek.
              </p>
            ) : (
              <div className="space-y-2 max-h-[50vh] overflow-y-auto">
                {filteredBodyweight.map((exercise) => (
                  <button
                    key={exercise.id}
                    type="button"
                    onClick={() => handleSelectBodyweightExercise(exercise)}
                    className="w-full text-left p-3 bg-gray-50 dark:bg-gray-700 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors border border-transparent hover:border-primary-200 dark:hover:border-primary-700"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900 dark:text-gray-100">{exercise.name}</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{exercise.muscleGroup}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {exercise.subcategory && (
                          <span className="text-lg">
                            {bodyweightSubcategories.find(s => s.id === exercise.subcategory)?.icon}
                          </span>
                        )}
                        <Plus className="w-5 h-5 text-gray-400" />
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </Modal>

      {/* Add Set Modal */}
      <Modal
        isOpen={showSetModal}
        onClose={() => setShowSetModal(false)}
        title="Sorozat rögzítése"
        footer={
          <>
            <button
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              onClick={() => setShowSetModal(false)}
            >
              Mégse
            </button>
            <Button type="submit" form="set-form" loading={savingSet}>
              Rögzítés
            </Button>
          </>
        }
      >
        {/* Exercise name badge */}
        {selectedExerciseInfo.name && (
          <div className="-mt-2 mb-2 text-center">
            <span className="inline-block text-sm font-semibold text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 px-3 py-1 rounded-lg">
              {selectedExerciseInfo.name}
            </span>
          </div>
        )}
        <form id="set-form" onSubmit={handleAddSet}>
          {/* Set Type Tabs - Different UI for bodyweight vs equipment */}
          {isBodyweightExercise ? (
            /* Bodyweight: Simplified tabs - Ismétlés / Időtartam */
            <div className="flex border-b border-gray-200 mb-4">
              <button
                type="button"
                className={`flex-1 py-2 px-3 text-sm font-medium border-b-2 transition-colors ${
                  setType === 'standard'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setSetType('standard')}
              >
                Ismétlés
              </button>
              <button
                type="button"
                className={`flex-1 py-2 px-3 text-sm font-medium border-b-2 transition-colors ${
                  setType === 'cardio'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setSetType('cardio')}
              >
                Időtartam
              </button>
            </div>
          ) : (
            /* Equipment: Full tabs - Alap / Kardió / Két kéz */
            <div className="flex border-b border-gray-200 mb-4">
              <button
                type="button"
                className={`flex-1 py-2 px-3 text-sm font-medium border-b-2 transition-colors ${
                  setType === 'standard'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setSetType('standard')}
              >
                Alap
              </button>
              <button
                type="button"
                className={`flex-1 py-2 px-3 text-sm font-medium border-b-2 transition-colors ${
                  setType === 'cardio'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setSetType('cardio')}
              >
                Kardió
              </button>
              <button
                type="button"
                className={`flex-1 py-2 px-3 text-sm font-medium border-b-2 transition-colors ${
                  setType === 'dualHand'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setSetType('dualHand')}
              >
                Két kéz
              </button>
            </div>
          )}

          {/* Standard/Ismétlés Set Fields */}
          {setType === 'standard' && (
            <div className={isBodyweightExercise ? 'space-y-4 mb-4' : 'grid grid-cols-2 gap-4 mb-4'}>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Ismétlések száma
                </label>
                <input
                  type="number"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  value={reps}
                  onChange={(e) => setReps(e.target.value)}
                  min="1"
                  max="100"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Súly (kg){isBodyweightExercise && <span className="text-gray-400 font-normal"> - opcionális</span>}
                </label>
                <input
                  type="number"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                  min="0"
                  max="1000"
                  step="0.5"
                  placeholder={isBodyweightExercise ? 'pl. súlymellény' : ''}
                />
                {isBodyweightExercise && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Súlymellény, hátizsák, lábnehezék esetén
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Cardio/Időtartam Set Fields */}
          {setType === 'cardio' && (
            <div className="space-y-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Időtartam
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="flex items-center">
                      <input
                        type="number"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                        value={durationMinutes}
                        onChange={(e) => setDurationMinutes(e.target.value)}
                        min="0"
                        max="999"
                        required
                      />
                      <span className="ml-2 text-gray-600 dark:text-gray-400">perc</span>
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center">
                      <input
                        type="number"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                        value={durationSeconds}
                        onChange={(e) => setDurationSeconds(e.target.value)}
                        min="0"
                        max="59"
                        required
                      />
                      <span className="ml-2 text-gray-600 dark:text-gray-400">mp</span>
                    </div>
                  </div>
                </div>
              </div>
              {/* Resistance level only for equipment */}
              {!isBodyweightExercise && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Gép fokozata (opcionális)
                  </label>
                  <input
                    type="number"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    value={resistanceLevel}
                    onChange={(e) => setResistanceLevel(e.target.value)}
                    min="1"
                    max="30"
                    placeholder="pl. 5"
                  />
                </div>
              )}
            </div>
          )}

          {/* Dual Hand Set Fields */}
          {setType === 'dualHand' && (
            <div className="space-y-4 mb-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-blue-50 p-3 rounded-lg">
                  <p className="text-xs font-semibold text-blue-700 mb-2 text-center">Bal kéz</p>
                  <div className="space-y-2">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Ismétlés</label>
                      <input
                        type="number"
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                        value={leftReps}
                        onChange={(e) => setLeftReps(e.target.value)}
                        min="0"
                        max="100"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Súly (kg)</label>
                      <input
                        type="number"
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                        value={leftWeight}
                        onChange={(e) => setLeftWeight(e.target.value)}
                        min="0"
                        max="500"
                        step="0.5"
                      />
                    </div>
                  </div>
                </div>
                <div className="bg-green-50 p-3 rounded-lg">
                  <p className="text-xs font-semibold text-green-700 mb-2 text-center">Jobb kéz</p>
                  <div className="space-y-2">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Ismétlés</label>
                      <input
                        type="number"
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                        value={rightReps}
                        onChange={(e) => setRightReps(e.target.value)}
                        min="0"
                        max="100"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Súly (kg)</label>
                      <input
                        type="number"
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                        value={rightWeight}
                        onChange={(e) => setRightWeight(e.target.value)}
                        min="0"
                        max="500"
                        step="0.5"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Difficulty (common for all types) */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nehézség (1-5)
            </label>
            <div className="grid grid-cols-5 gap-2">
              {[
                { level: 1, emoji: '😄' },
                { level: 2, emoji: '🙂' },
                { level: 3, emoji: '😐' },
                { level: 4, emoji: '😓' },
                { level: 5, emoji: '🥵' },
              ].map(({ level, emoji }) => (
                <button
                  key={level}
                  type="button"
                  className={`flex flex-col items-center justify-center py-2 px-1 rounded-lg font-semibold transition-all border-2 ${
                    difficulty === level
                      ? level === 1 ? 'bg-green-100 border-green-500 text-green-700'
                        : level === 2 ? 'bg-lime-100 border-lime-500 text-lime-700'
                        : level === 3 ? 'bg-yellow-100 border-yellow-500 text-yellow-700'
                        : level === 4 ? 'bg-orange-100 border-orange-500 text-orange-700'
                        : 'bg-red-100 border-red-500 text-red-700'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400'
                  }`}
                  onClick={() => setDifficulty(level)}
                >
                  <span className="text-xl mb-0.5">{emoji}</span>
                  <span className="text-sm">{level}</span>
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-500 text-center mt-2">
              1 = Könnyű, 5 = Nagyon nehéz
            </p>
          </div>
        </form>
      </Modal>

      {/* Edit Set Modal */}
      <Modal
        isOpen={showEditSetModal}
        onClose={() => { setShowEditSetModal(false); setEditingSetId(null); setEditingExerciseId(null); }}
        title="Sorozat szerkesztése"
        footer={
          <>
            <button
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              onClick={() => { setShowEditSetModal(false); setEditingSetId(null); setEditingExerciseId(null); }}
            >
              Mégse
            </button>
            <Button type="submit" form="edit-set-form" loading={savingSet}>
              Mentés
            </Button>
          </>
        }
      >
        <form id="edit-set-form" onSubmit={handleEditSet}>
          {/* Standard Set Fields */}
          {setType === 'standard' && (
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ismétlések száma
                </label>
                <input
                  type="number"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  value={reps}
                  onChange={(e) => setReps(e.target.value)}
                  min="1"
                  max="100"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Súly (kg)
                </label>
                <input
                  type="number"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                  min="0"
                  max="1000"
                  step="0.5"
                />
              </div>
            </div>
          )}

          {/* Cardio Set Fields */}
          {setType === 'cardio' && (
            <div className="space-y-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Időtartam
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="flex items-center">
                      <input
                        type="number"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                        value={durationMinutes}
                        onChange={(e) => setDurationMinutes(e.target.value)}
                        min="0"
                        max="999"
                        required
                      />
                      <span className="ml-2 text-gray-600">perc</span>
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center">
                      <input
                        type="number"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                        value={durationSeconds}
                        onChange={(e) => setDurationSeconds(e.target.value)}
                        min="0"
                        max="59"
                        required
                      />
                      <span className="ml-2 text-gray-600">mp</span>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Gép fokozata (opcionális)
                </label>
                <input
                  type="number"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  value={resistanceLevel}
                  onChange={(e) => setResistanceLevel(e.target.value)}
                  min="1"
                  max="30"
                  placeholder="pl. 5"
                />
              </div>
            </div>
          )}

          {/* Dual Hand Set Fields */}
          {setType === 'dualHand' && (
            <div className="space-y-4 mb-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-blue-50 p-3 rounded-lg">
                  <p className="text-xs font-semibold text-blue-700 mb-2 text-center">Bal kéz</p>
                  <div className="space-y-2">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Ismétlés</label>
                      <input
                        type="number"
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                        value={leftReps}
                        onChange={(e) => setLeftReps(e.target.value)}
                        min="0"
                        max="100"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Súly (kg)</label>
                      <input
                        type="number"
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                        value={leftWeight}
                        onChange={(e) => setLeftWeight(e.target.value)}
                        min="0"
                        max="500"
                        step="0.5"
                      />
                    </div>
                  </div>
                </div>
                <div className="bg-green-50 p-3 rounded-lg">
                  <p className="text-xs font-semibold text-green-700 mb-2 text-center">Jobb kéz</p>
                  <div className="space-y-2">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Ismétlés</label>
                      <input
                        type="number"
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                        value={rightReps}
                        onChange={(e) => setRightReps(e.target.value)}
                        min="0"
                        max="100"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Súly (kg)</label>
                      <input
                        type="number"
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                        value={rightWeight}
                        onChange={(e) => setRightWeight(e.target.value)}
                        min="0"
                        max="500"
                        step="0.5"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Difficulty (common for all types) */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nehézség (1-5)
            </label>
            <div className="grid grid-cols-5 gap-2">
              {[
                { level: 1, emoji: '😄' },
                { level: 2, emoji: '🙂' },
                { level: 3, emoji: '😐' },
                { level: 4, emoji: '😓' },
                { level: 5, emoji: '🥵' },
              ].map(({ level, emoji }) => (
                <button
                  key={level}
                  type="button"
                  className={`flex flex-col items-center justify-center py-2 px-1 rounded-lg font-semibold transition-all border-2 ${
                    difficulty === level
                      ? level === 1 ? 'bg-green-100 border-green-500 text-green-700'
                        : level === 2 ? 'bg-lime-100 border-lime-500 text-lime-700'
                        : level === 3 ? 'bg-yellow-100 border-yellow-500 text-yellow-700'
                        : level === 4 ? 'bg-orange-100 border-orange-500 text-orange-700'
                        : 'bg-red-100 border-red-500 text-red-700'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400'
                  }`}
                  onClick={() => setDifficulty(level)}
                >
                  <span className="text-xl mb-0.5">{emoji}</span>
                  <span className="text-sm">{level}</span>
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-500 text-center mt-2">
              1 = Könnyű, 5 = Nagyon nehéz
            </p>
          </div>
        </form>
      </Modal>

      {/* Delete Set Confirm Modal */}
      <Modal
        isOpen={showDeleteSetModal}
        onClose={() => { setShowDeleteSetModal(false); setDeletingSetId(null); setDeletingExerciseId(null); }}
        title="Sorozat törlése"
        footer={
          <>
            <button
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              onClick={() => { setShowDeleteSetModal(false); setDeletingSetId(null); setDeletingExerciseId(null); }}
            >
              Mégse
            </button>
            <Button 
              variant="danger" 
              loading={deletingSet}
              onClick={handleConfirmDeleteSet}
            >
              Törlés
            </Button>
          </>
        }
      >
        <div className="text-center py-4">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Trash2 className="w-8 h-8 text-red-600" />
          </div>
          <p className="text-gray-700 text-lg mb-2">
            Biztosan törlöd ezt a sorozatot?
          </p>
          <p className="text-gray-500 text-sm">
            A törlés nem vonható vissza.
          </p>
        </div>
      </Modal>

      {/* Confirm End Modal */}
      <Modal
        isOpen={showConfirmEndModal}
        onClose={() => setShowConfirmEndModal(false)}
        title={isBackdateMode ? "Korábbi edzés mentése" : "Edzés befejezése"}
        footer={
          <>
            <button
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              onClick={() => setShowConfirmEndModal(false)}
            >
              Mégse
            </button>
            <Button 
              variant="danger" 
              onClick={handleConfirmEnd}
            >
              {isBackdateMode ? "Igen, mentés" : "Igen, befejezem"}
            </Button>
          </>
        }
      >
        <div className="text-center py-4">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Square className="w-8 h-8 text-red-600" />
          </div>
          <p className="text-gray-700 text-lg mb-2">
            {isBackdateMode ? "Biztosan menteni szeretnéd az edzést?" : "Biztosan be akarod fejezni az edzést?"}
          </p>
          {!isBackdateMode && (
            <p className="text-gray-500 text-sm">
              Az edzés időtartama: <span className="font-semibold">{elapsedTime}</span>
            </p>
          )}
          <p className="text-gray-500 text-sm">
            Gyakorlatok száma: <span className="font-semibold">{activeWorkout?.exercises.length || 0}</span>
          </p>
          
          <div className="mt-4 pt-4 border-t border-gray-200">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowAbandonConfirmModal(true)}
              className="text-gray-500 hover:text-red-600"
            >
              Edzés megszakítása mentés nélkül
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Exercise Modal */}
      <Modal
        isOpen={showDeleteExerciseModal}
        onClose={() => setShowDeleteExerciseModal(false)}
        title="Gyakorlat törlése"
        footer={
          <>
            <button
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              onClick={() => setShowDeleteExerciseModal(false)}
            >
              Mégse
            </button>
            <Button variant="danger" loading={deletingExercise} onClick={handleConfirmDeleteExercise}>
              Törlés
            </Button>
          </>
        }
      >
        <p className="text-gray-700">
          Biztosan törlöd a <strong>{deleteExerciseName}</strong> gyakorlatot és az összes sorozatát?
        </p>
      </Modal>

      {/* Inactivity Modal */}
      <Modal
        isOpen={showInactivityModal}
        onClose={() => {
          recordActivity();
        }}
        title="Inaktivitás észlelve"
        footer={
          <Button onClick={() => recordActivity()}>
            Edzés folytatása
          </Button>
        }
      >
        <div className="text-center py-4">
          <Clock className="w-12 h-12 text-amber-500 mx-auto mb-3" />
          <p className="text-gray-700 mb-2">
            30 perce nem történt aktivitás az edzésben.
          </p>
          <p className="text-gray-500 text-sm">
            Az edzés automatikusan befejeződik <strong>{inactivityCountdown}</strong> másodperc múlva, ha nem folytatod.
          </p>
        </div>
      </Modal>

      {/* Abandon Workout Confirm Modal */}
      <Modal
        isOpen={showAbandonConfirmModal}
        onClose={() => setShowAbandonConfirmModal(false)}
        title="Edzés megszakítása"
        footer={
          <>
            <button
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              onClick={() => setShowAbandonConfirmModal(false)}
            >
              Mégse
            </button>
            <Button 
              variant="danger" 
              loading={abandoningWorkout}
              onClick={handleAbandonWorkout}
            >
              Igen, megszakítom
            </Button>
          </>
        }
      >
        <div className="text-center py-4">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Trash2 className="w-8 h-8 text-red-600" />
          </div>
          <p className="text-gray-700 text-lg mb-2">
            Biztosan meg akarod szakítani az edzést mentés nélkül?
          </p>
          <p className="text-red-500 text-sm font-medium">
            Figyelem: Ez a művelet nem vonható vissza, az edzés összes adata törlődik!
          </p>
        </div>
      </Modal>

      {/* End Workout Modal */}
      <Modal
        isOpen={showEndModal}
        onClose={() => setShowEndModal(false)}
        title={isBackdateMode ? "Korábbi edzés mentése" : "Edzés befejezése - Összegzés"}
        size={isBackdateMode && backdateTimeDistribution === 'manual' ? 'xl' : 'lg'}
        footer={
          <>
            <button
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              onClick={() => setShowEndModal(false)}
            >
              Mégse
            </button>
            <Button 
              variant="danger" 
              loading={endingWorkout}
              onClick={handleEndWorkout}
            >
              Mentés és befejezés
            </Button>
          </>
        }
      >
        {/* Normal mode: show elapsed time */}
        {!isBackdateMode && (
          <div className="text-center mb-6">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Clock className="w-6 h-6 text-primary-600" />
              <span className="text-2xl font-mono font-bold text-primary-600">
                {elapsedTime}
              </span>
            </div>
            <p className="text-gray-600">
              Összesen {activeWorkout?.exercises.length || 0} gyakorlat
            </p>
          </div>
        )}

        {/* Backdate mode: duration input */}
        {isBackdateMode && (
          <div className="mb-6 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <Clock className="w-5 h-5 text-amber-600" />
              <span className="font-medium text-amber-800 dark:text-amber-300">Edzés időtartama</span>
            </div>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">Óra</label>
                <input
                  type="number"
                  value={backdateDurationHours}
                  onChange={(e) => setBackdateDurationHours(e.target.value)}
                  min="0"
                  max="10"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">Perc</label>
                <input
                  type="number"
                  value={backdateDurationMinutes}
                  onChange={(e) => setBackdateDurationMinutes(e.target.value)}
                  min="0"
                  max="59"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
            
            {/* Exercise time distribution */}
            {activeWorkout && activeWorkout.exercises.length > 0 && (
              <>
                <div className="border-t border-amber-200 dark:border-amber-700 pt-3 mt-3">
                  <label className="block text-sm font-medium text-amber-800 dark:text-amber-300 mb-2">
                    Gyakorlatok időelosztása
                  </label>
                  <div className="flex gap-2 mb-3">
                    <button
                      type="button"
                      className={`flex-1 py-2 px-3 text-sm rounded-lg border-2 transition-all ${
                        backdateTimeDistribution === 'proportional'
                          ? 'bg-primary-100 border-primary-500 text-primary-700'
                          : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                      }`}
                      onClick={() => setBackdateTimeDistribution('proportional')}
                    >
                      Arányos szétosztás
                    </button>
                    <button
                      type="button"
                      className={`flex-1 py-2 px-3 text-sm rounded-lg border-2 transition-all ${
                        backdateTimeDistribution === 'manual'
                          ? 'bg-primary-100 border-primary-500 text-primary-700'
                          : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                      }`}
                      onClick={() => setBackdateTimeDistribution('manual')}
                    >
                      Egyéni megadás
                    </button>
                  </div>
                  
                  {backdateTimeDistribution === 'proportional' && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      A teljes időtartam egyenlően oszlik el a {activeWorkout.exercises.length} gyakorlat között.
                    </p>
                  )}
                  
                  {backdateTimeDistribution === 'manual' && (
                    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 max-h-48 overflow-y-auto">
                      {activeWorkout.exercises.map((exercise, index) => (
                        <div 
                          key={exercise.id}
                          className={`flex items-center justify-between p-2 ${
                            index !== activeWorkout.exercises.length - 1 ? 'border-b border-gray-100 dark:border-gray-700' : ''
                          }`}
                        >
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            <span className="flex-shrink-0 w-6 h-6 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center text-xs font-semibold">
                              {index + 1}
                            </span>
                            <span className="text-sm text-gray-700 dark:text-gray-300 truncate">
                              {exercise.bodyweightExercise?.name || exercise.machine?.name}
                            </span>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <input
                              type="number"
                              value={backdateExerciseTimes[exercise.id] || 0}
                              onChange={(e) => setBackdateExerciseTimes(prev => ({
                                ...prev,
                                [exercise.id]: parseInt(e.target.value, 10) || 0
                              }))}
                              min="0"
                              max="120"
                              className="w-16 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                            />
                            <span className="text-xs text-gray-500">perc</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2 text-center">
            {isBackdateMode ? "Hogyan érezted magad az edzés után?" : "Hogyan érzed magad az edzés után?"}
          </label>
          <div className="grid grid-cols-5 gap-2">
            {moodOptions.map(({ level, emoji, label }) => (
              <button
                key={level}
                type="button"
                className={`flex flex-col items-center justify-center py-2 px-1 rounded-lg font-semibold transition-all border-2 ${
                  endMood === level
                    ? level === 1 ? 'bg-red-100 border-red-500 text-red-700'
                      : level === 2 ? 'bg-orange-100 border-orange-500 text-orange-700'
                      : level === 3 ? 'bg-yellow-100 border-yellow-500 text-yellow-700'
                      : level === 4 ? 'bg-lime-100 border-lime-500 text-lime-700'
                      : 'bg-green-100 border-green-500 text-green-700'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400'
                }`}
                onClick={() => setEndMood(level)}
              >
                <span className="text-xl mb-0.5">{emoji}</span>
                <span className="text-xs">{label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Body weight - optional */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Testsúly (opcionális)
          </label>
          <div className="flex gap-2">
            <input
              type="number"
              className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="pl. 85"
              value={bodyWeight}
              onChange={(e) => setBodyWeight(e.target.value)}
              min="20"
              max="500"
              step="0.1"
            />
            <span className="flex items-center px-3 py-2 bg-gray-100 dark:bg-gray-600 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300">
              kg
            </span>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Rögzítsd a testsúlyodat a változás követéséhez
          </p>
        </div>

        {/* Notes - optional */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Megjegyzés (opcionális)
          </label>
          <textarea
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
            placeholder="Írj megjegyzést az edzésről..."
            value={workoutNotes}
            onChange={(e) => setWorkoutNotes(e.target.value)}
            rows={3}
            maxLength={1000}
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {workoutNotes.length}/1000 karakter
          </p>
        </div>
      </Modal>
    </div>
  );
};

export default WorkoutSession;
